import React, { useEffect, useMemo, useRef, useState, useTransition } from "react";
import API_BASE from "../../utils/api";

/* ----------------------------- helpers ----------------------------- */
const clamp = (v, lo = 0, hi = 1e9) =>
  Math.max(lo, Math.min(hi, Number.isFinite(+v) ? +v : lo));

const num = (v) => {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const s = String(v)
    .replace(/\$/g, "")
    .replace(/[,  \s]/g, "")
    .replace(/%/g, "")
    .trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const pctFrac = (v) => {
  if (v == null || v === "") return 0;
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return 0;
    return v > 1 ? clamp(v / 100, 0, 1) : clamp(v, 0, 1);
  }
  const s = String(v).trim();
  if (!s) return 0;
  const stripped = s.replace(/[%\s]/g, "");
  const n = Number(stripped);
  if (!Number.isFinite(n)) return 0;
  if (/%/.test(s)) return clamp(n / 100, 0, 1);
  return n <= 1 ? clamp(n, 0, 1) : clamp(n / 100, 0, 1);
};

const fmt0 = (n) => (Number.isFinite(n) ? Number(n).toLocaleString() : "—");
const fmt1 = (n) => (Number.isFinite(n) ? Number(n).toFixed(1) : "—");
const fmtPct = (n, d = 1) => (Number.isFinite(n) ? `${Number(n).toFixed(d)}%` : "—");

const cx = (...classes) => classes.filter(Boolean).join(" ");

const escapeCsvCell = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const toCsv = (rows) => rows.map((r) => r.map(escapeCsvCell).join(",")).join("\n");

function download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function csvToRows(text) {
  const out = [];
  let i = 0;
  let cur = "";
  let row = [];
  let inQuotes = false;

  while (i < text.length) {
    const ch = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cur += '"';
        i += 2;
        continue;
      }
      if (ch === '"') {
        inQuotes = false;
        i += 1;
        continue;
      }
      cur += ch;
      i += 1;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ",") {
      row.push(cur);
      cur = "";
      i += 1;
      continue;
    }
    if (ch === "\n") {
      row.push(cur);
      out.push(row);
      row = [];
      cur = "";
      i += 1;
      continue;
    }
    if (ch === "\r") {
      i += 1;
      continue;
    }

    cur += ch;
    i += 1;
  }

  row.push(cur);
  out.push(row);

  return out.filter((r) => r.some((x) => String(x || "").trim() !== ""));
}

const stripAccents = (s) =>
  String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const normName = (s) =>
  stripAccents(String(s || ""))
    .toLowerCase()
    .replace(/\u2019/g, "'")
    .replace(/\./g, "")
    .replace(/,\s*(jr|sr)\b/g, "")
    .replace(/\b(jr|sr)\b/g, "")
    .replace(/[^a-z0-9' -]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const normTeam = (s) => {
  const team = String(s || "").toUpperCase().trim();
  return team === "JAC" ? "JAX" : team;
};

// Vegas timestamps without an offset are Eastern wall-clock times.
function slateKickoffStamp(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (match) {
    return Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5], +(match[6] || 0));
  }
  if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(raw)) return NaN;
  const instant = new Date(raw);
  if (!Number.isFinite(+instant)) return NaN;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(instant);
  const fields = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return Date.UTC(+fields.year, +fields.month - 1, +fields.day, +fields.hour, +fields.minute, +fields.second);
}

function mergeSimulationProjections(projectionData, simulationData, slate, vegasData) {
  const rowsOf = (data) => Array.isArray(data) ? data : data?.rows ?? data?.players ?? data?.data ?? [];
  const mainRows = rowsOf(projectionData);
  const schedule = new Map(rowsOf(vegasData).map((game) => [normTeam(game.team ?? game.Team), game]));
  const simRows = rowsOf(simulationData);
  const position = (row) => String(row.pos ?? row.Pos ?? row.Slot ?? row.slot ?? "").toUpperCase().replace(/\d+$/, "");
  const player = (row) => row.player ?? row.Player ?? row.name ?? row.Name ?? "";
  const key = (row) => {
    const pos = position(row);
    const team = normTeam(row.team ?? row.Team);
    return `${team}|${pos}|${pos === "DST" ? "DST" : normName(player(row))}`;
  };
  const simulations = new Map(simRows.map((row) => [key(row), row]));
  const seen = new Set();
  const merge = (base, sim) => {
    const row = { ...base };
    if (sim) {
      for (const site of ["dk", "fd"]) {
        for (const field of ["proj", "floor", "ceiling"]) {
          const value = sim[`${site}_${field}`];
          if (value != null && Number.isFinite(Number(value))) row[`${site}_${field}`] = Number(value);
        }
      }
      row.pos = position(sim);
      row.team = normTeam(sim.team ?? sim.Team);
      row.hasSimulation = true;
    }
    const game = schedule.get(normTeam(row.team ?? row.Team));
    if (game) {
      row.opp = normTeam(game.opponent ?? game.opp ?? row.opp);
      row.time = formatVegasTime(game.gameDate ?? game.game_date) || row.time || "";
    }
    row.ratesAvailable = slate === "main" && !!base.hasMainRates;
    if (!row.ratesAvailable) {
      for (const site of ["dk", "fd"]) {
        row[`${site}_pown`] = null;
        row[`${site}_opt`] = null;
      }
      for (const k of ["DK pOWN%", "FD pOWN%", "DK pOWN", "FD pOWN", "DK Opt%", "FD Opt%", "DK Opt", "FD Opt"]) row[k] = null;
    }
    return row;
  };
  const out = mainRows.map((row) => {
    const k = key(row); seen.add(k);
    return merge({ ...row, hasMainRates: true }, simulations.get(k));
  });
  for (const sim of simRows) {
    if (seen.has(key(sim))) continue;
    out.push(merge({ player: player(sim), team: normTeam(sim.team ?? sim.Team), pos: position(sim), hasMainRates: false }, sim));
  }
  return out;
}

function buildSlateDetails(siteIds, vegasData, site) {
  const players = Array.isArray(siteIds?.[site]) ? siteIds[site] : siteIds?.sites?.[site] ?? [];
  const schedule = Array.isArray(vegasData) ? vegasData
    : vegasData?.rows ?? vegasData?.players ?? vegasData?.data ?? [];
  const byTeam = new Map();
  for (const game of schedule) {
    const team = normTeam(game.team ?? game.Team);
    const opponent = normTeam(game.opponent ?? game.Opponent ?? game.opp ?? game.Opp);
    if (!team || !opponent) continue;
    const key = [team, opponent].sort().join("@");
    const stamp = slateKickoffStamp(game.gameDate ?? game.game_date);
    byTeam.set(team, { key, stamp });
    byTeam.set(opponent, { key, stamp });
  }
  const details = {};
  for (const slate of ["main", "early", "late", "thu_mon"]) {
    const idKey = slate === "main" ? "id" : `${slate}_id`;
    const salaryKey = slate === "main" ? "salary" : `${slate}_salary`;
    const teams = new Set(players.filter((player) => {
      const salary = Number(player[salaryKey]);
      return player[idKey] && salary > 0 && salary < 50000;
    }).map((player) => normTeam(player.team ?? player.Team)));
    const games = new Map();
    let complete = teams.size > 0;
    for (const team of teams) {
      const game = byTeam.get(team);
      if (!game) { complete = false; continue; }
      games.set(game.key, game.stamp);
    }
    if (!complete) { details[slate] = null; continue; }
    const stamps = [...games.values()];
    const first = stamps.every(Number.isFinite) ? Math.min(...stamps) : NaN;
    const kickoff = Number.isFinite(first) ? new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC", weekday: "short", hour: "numeric", minute: "2-digit", hour12: true,
    }).format(new Date(first)).replace(",", "") + " ET" : "";
    details[slate] = { games: games.size, kickoff };
  }
  return details;
}


const normPos = (s) => {
  const raw = String(s || "")
    .toUpperCase()
    .replace("/FLEX", "")
    .trim();
  if (raw === "D" || raw === "DEF" || raw === "DEFENSE" || raw.includes("D/ST") || raw === "DST") {
    return "DST";
  }
  return raw;
};

function parseKick(timeStr) {
  const s = String(timeStr || "").trim();
  if (!s) return NaN;

  const asDate = new Date(s);
  if (!Number.isNaN(asDate.getTime())) {
    return asDate.getHours() * 60 + asDate.getMinutes();
  }

  const m = s.match(/(\d{1,2}):(\d{2})\s*([AP])M/i);
  if (m) {
    let h = Number(m[1]) % 12;
    const mi = Number(m[2]);
    if (/p/i.test(m[3])) h += 12;
    return h * 60 + mi;
  }

  return NaN;
}

function sameDayHasStarted(timeStr) {
  const raw = String(timeStr || "").trim();
  if (!raw) return false;

  const parsedDate = new Date(raw);
  if (!Number.isNaN(parsedDate.getTime())) {
    return Date.now() >= parsedDate.getTime();
  }

  const mins = parseKick(raw);
  if (!Number.isFinite(mins)) return false;

  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  return nowMins >= mins;
}

function formatKick(timeStr) {
  const s = String(timeStr || "").trim();
  if (!s) return "";

  const asDate = new Date(s);
  if (!Number.isNaN(asDate.getTime())) {
    return asDate.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  return s;
}

function parseVegasDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatVegasTime(value) {
  const d = parseVegasDate(value);
  if (!d) return "—";
  const hour24 = d.getHours();
  const minute = d.getMinutes();
  const hour12 = hour24 % 12 || 12;
  const suffix = hour24 >= 12 ? "PM" : "AM";
  return `${hour12}:${String(minute).padStart(2, "0")} ${suffix}`;
}

function vegasKickMinutes(value) {
  const d = parseVegasDate(value);
  return d ? d.getHours() * 60 + d.getMinutes() : Number.POSITIVE_INFINITY;
}

function gameKey(a, b) {
  const A = normTeam(a);
  const B = normTeam(b);
  return A <= B ? `${A}|${B}` : `${B}|${A}`;
}

function pickFirst(obj, keys) {
  if (!obj || typeof obj !== "object") return undefined;
  for (const k of keys) {
    if (obj[k] != null && String(obj[k]).trim() !== "") return obj[k];
  }
  return undefined;
}

/* --------------------------- sticky state -------------------------- */
const useStickyState = (key, init) => {
  const initRef = React.useRef(init);

  const readFromStorage = React.useCallback(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw == null) return initRef.current;
      const parsed = JSON.parse(raw);
      return parsed ?? initRef.current;
    } catch {
      return initRef.current;
    }
  }, [key]);

  const [v, setV] = useState(() => readFromStorage());

  useEffect(() => {
    setV(readFromStorage());
  }, [key, readFromStorage]);

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(v));
    } catch {}
  }, [key, v]);

  return [v, setV];
};

const useStickySet = (key, init = []) => {
  const [arr, setArr] = useStickyState(key, init);
  const set = useMemo(() => new Set(arr), [arr]);

  const setSet = (updater) => {
    setArr((prevArr) => {
      const prevSet = new Set(prevArr || []);
      const next =
        typeof updater === "function"
          ? updater(prevSet)
          : updater instanceof Set
          ? updater
          : new Set(updater || []);
      return Array.from(next || []);
    });
  };

  return [set, setSet];
};

const useStickyDict = (key, initObj = {}) => useStickyState(key, initObj);

/* ------------------------------ fetchers ------------------------------ */
function useJson(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.text();
        const cleaned = raw.replace(/^\uFEFF/, "").trim();
        const j = cleaned ? JSON.parse(cleaned) : null;
        if (alive) {
          setData(j);
          setErr(null);
        }
      } catch (e) {
        if (alive) {
          setData(null);
          setErr(e);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);

  return { data, err };
}

function useLastUpdatedFromMeta(metaUrl) {
  const [text, setText] = useState("—");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) return;
        const j = await r.json();
        const iso =
          j?.updated_iso ||
          j?.updated_utc ||
          j?.source_mtime_iso ||
          j?.last_updated ||
          j?.generated_at ||
          j?.timestamp;

        if (!iso) return;
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return;

        const t = d.toLocaleString(undefined, {
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        });
        if (alive) setText(t);
      } catch {}
    })();

    return () => {
      alive = false;
    };
  }, [metaUrl]);

  return text;
}

/* ----------------------------- sources ----------------------------- */
const PROJ_SRC = "/data/nfl/classic/latest/projections.json";
const SITE_IDS_SRC = "/data/nfl/classic/latest/site_ids.json";
const META_SRC = "/data/nfl/classic/latest/meta.json";
const QB_DATA_SRC = "/data/nfl/classic/latest/qb_data.json";
const VEGAS_SRC = "/data/nfl/classic/latest/vegas.json";

/* ------------------------------ theming ----------------------------- */
const OPT_LIGHT_THEME = {
  page: "min-h-screen bg-slate-100 text-slate-900",
  headerText: "text-slate-900",
  subtext: "text-slate-600",
};

const OPT_DARK_THEME = {
  page: "min-h-screen bg-slate-950 text-slate-100",
  headerText: "text-slate-100",
  subtext: "text-slate-400",
};

const UI = {
  card: "rounded-2xl border shadow-sm",
  btn: {
    primary: "h-9 px-4 rounded-md font-semibold",
    ghost: "h-9 px-3 rounded-md border text-sm font-semibold",
  },
  inputBase:
    "h-9 px-3 text-sm rounded-md border focus:outline-none focus:ring-2 focus:ring-sky-300/40 focus:border-sky-400",
};

const glass = {
  shell: "bg-white/5 backdrop-blur-xl border border-white/10",
  active:
    "bg-white/10 backdrop-blur-md border border-white/20 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]",
  idle:
    "bg-white/5 backdrop-blur-md border border-white/10 text-slate-300 hover:bg-white/8",
  idleSoft:
    "border border-transparent text-slate-300 hover:bg-white/6 hover:border-white/10",
};

const TEAM_COLORS = {
  ARI: "#97233F", ATL: "#A71930", BAL: "#241773", BUF: "#00338D", CAR: "#0085CA",
  CHI: "#0B162A", CIN: "#FB4F14", CLE: "#311D00", DAL: "#041E42", DEN: "#FB4F14",
  DET: "#0076B6", GB: "#203731", HOU: "#03202F", IND: "#002C5F", JAX: "#006778",
  KC: "#E31837", LAC: "#0080C6", LAR: "#003594", LV: "#000000", MIA: "#008E97",
  MIN: "#4F2683", NE: "#002244", NO: "#D3BC8D", NYG: "#0B2265", NYJ: "#125740",
  PHI: "#004C54", PIT: "#FFB612", SEA: "#002244", SF: "#AA0000", TB: "#D50A0A",
  TEN: "#0C2340", WAS: "#5A1414",
};

const chipStyle = (abbr, active = false) => {
  const bg = TEAM_COLORS[abbr] || "#64748b";
  return {
    background: bg,
    color: "#fff",
    borderRadius: 6,
    padding: "1px 7px",
    fontSize: "11px",
    lineHeight: "16px",
    border: active ? "2px solid rgba(255,255,255,0.85)" : "1px solid rgba(0,0,0,0.08)",
    boxShadow: active ? "0 0 0 2px rgba(0,0,0,0.08) inset" : "none",
  };
};

/* ------------------------------ sites ------------------------------ */
const SITES = {
  dk: {
    key: "dk",
    label: "DraftKings",
    logo: "/logos/dk.png",
    cap: 50000,
    slots: [
      { name: "QB", eligible: ["QB"] },
      { name: "RB1", eligible: ["RB"] },
      { name: "RB2", eligible: ["RB"] },
      { name: "WR1", eligible: ["WR"] },
      { name: "WR2", eligible: ["WR"] },
      { name: "WR3", eligible: ["WR"] },
      { name: "TE", eligible: ["TE"] },
      { name: "FLEX", eligible: ["RB", "WR", "TE"] },
      { name: "DST", eligible: ["DST"] },
    ],
    exportHeader: ["QB", "RB", "RB", "WR", "WR", "WR", "TE", "FLEX", "DST"],
    posKeys: ["Pos", "Position", "pos", "DK Pos"],
    projKeys: ["DK Proj", "Projection", "proj", "Points", "dk_proj"],
    pownKeys: ["DK pOWN%", "DK pOWN", "dk_pown", "dk_pown_pct"],
    salKeys: ["DK Sal", "DK Salary", "salary", "dk_salary"],
    floorKeys: ["DK Floor", "Floor", "dk_floor"],
    ceilKeys: ["DK Ceiling", "Ceiling", "dk_ceiling", "dk_ceil"],
  },
  fd: {
    key: "fd",
    label: "FanDuel",
    logo: "/logos/fd.png",
    cap: 60000,
    slots: [
      { name: "QB", eligible: ["QB"] },
      { name: "RB1", eligible: ["RB"] },
      { name: "RB2", eligible: ["RB"] },
      { name: "WR1", eligible: ["WR"] },
      { name: "WR2", eligible: ["WR"] },
      { name: "WR3", eligible: ["WR"] },
      { name: "TE", eligible: ["TE"] },
      { name: "FLEX", eligible: ["RB", "WR", "TE"] },
      { name: "DST", eligible: ["DST"] },
    ],
    exportHeader: ["QB", "RB", "RB", "WR", "WR", "WR", "TE", "FLEX", "DEF"],
    posKeys: ["Pos", "Position", "pos", "FD Pos"],
    projKeys: ["FD Proj", "Projection", "proj", "Points", "fd_proj"],
    pownKeys: ["FD pOWN%", "FD pOWN", "fd_pown", "fd_pown_pct"],
    salKeys: ["FD Sal", "FD Salary", "salary", "fd_salary"],
    floorKeys: ["FD Floor", "Floor", "fd_floor"],
    ceilKeys: ["FD Ceiling", "Ceiling", "fd_ceiling", "fd_ceil"],
  },
};

function splitEligible(raw) {
  const txt = String(raw || "").toUpperCase();
  if (!txt) return [];
  return Array.from(
    new Set(
      txt
        .replace(/\/FLEX/g, "")
        .replace(/\bD\b/g, "DST")
        .replace(/\bDEF\b/g, "DST")
        .replace(/\bDEFENSE\b/g, "DST")
        .replace(/\bD\/ST\b/g, "DST")
        .split(/[\/,;|]+/)
        .map((s) => normPos(s))
        .filter(Boolean)
    )
  );
}

function slotBase(slot) {
  const s = String(slot || "").toUpperCase();
  if (s.startsWith("RB")) return "RB";
  if (s.startsWith("WR")) return "WR";
  return s;
}

function canonicalSlotOrder(site) {
  return ["QB", "RB1", "RB2", "WR1", "WR2", "WR3", "TE", "FLEX", "DST"];
}

/* ------------------------ site ids / export ids ------------------------ */
function buildIdIndex(siteIds) {
  const out = {
    dk: new Map(),
    fd: new Map(),
    fdDisplay: new Map(),
    fdPrefix: null,
  };

  const dkList = Array.isArray(siteIds?.dk) ? siteIds.dk : siteIds?.sites?.dk ?? [];
  const fdList = Array.isArray(siteIds?.fd) ? siteIds.fd : siteIds?.sites?.fd ?? [];

  for (const r of dkList) {
    const id = String(r.id ?? r.ID ?? r.dk_id ?? r["DK ID"] ?? "").trim();
    const name = String(r.name ?? r.player ?? r.Player ?? "").trim();
    if (id && name) out.dk.set(name, id);
  }

  const counts = new Map();
  for (const r of fdList) {
    const px = r.slateId ?? r.slate_id ?? r.groupId ?? r.group_id ?? r.lid ?? r.prefix ?? null;
    if (px != null && px !== "") counts.set(String(px), (counts.get(String(px)) || 0) + 1);
  }
  if (counts.size === 1) out.fdPrefix = [...counts.keys()][0];
  else if (counts.size > 1) out.fdPrefix = [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];

  for (const r of fdList) {
    const id = String(r.id ?? r.ID ?? r.fd_id ?? r["FD ID"] ?? "").trim();
    const name = String(r.name ?? r.player ?? r.Player ?? "").trim();
    const display = String(r.displayName ?? r.Name ?? name).trim();
    if (id && name) {
      out.fd.set(name, id);
      out.fdDisplay.set(name, display);
    }
  }

  return out;
}

function fdNameWithIdRaw(name, id, prefix, display) {
  const outId = prefix ? `${prefix}-${id}` : id;
  return `${outId}:${display || name}`;
}

function parseExportCell(site, cell) {
  const raw = String(cell || "").trim();
  if (!raw) return { name: "", playerId: "", slateCode: "", lockedTag: false };

  if (site === "dk") {
    let s = raw;
    let lockedTag = false;

    if (/\(LOCKED\)\s*$/i.test(s)) {
      lockedTag = true;
      s = s.replace(/\(LOCKED\)\s*$/i, "").trim();
    }

    const m = s.match(/^(.*?)\s*\(([^()]+)\)\s*$/);
    if (m) {
      return {
        name: m[1].trim(),
        playerId: m[2].trim(),
        slateCode: "",
        lockedTag,
      };
    }

    if (/^\d+$/.test(s)) {
      return { name: "", playerId: s, slateCode: "", lockedTag };
    }

    return { name: s, playerId: "", slateCode: "", lockedTag };
  }

  let s = raw;
  let lockedTag = false;

  if (/\(LOCKED\)\s*$/i.test(s)) {
    lockedTag = true;
    s = s.replace(/\(LOCKED\)\s*$/i, "").trim();
  }

  const colonIdx = s.indexOf(":");
  if (colonIdx >= 0) {
    const left = s.slice(0, colonIdx).trim();
    const name = s.slice(colonIdx + 1).trim();

    const dashIdx = left.lastIndexOf("-");
    if (dashIdx >= 0) {
      return {
        name,
        slateCode: left.slice(0, dashIdx).trim(),
        playerId: left.slice(dashIdx + 1).trim(),
        lockedTag,
      };
    }

    return {
      name,
      slateCode: "",
      playerId: left,
      lockedTag,
    };
  }

  const slateIdOnly = s.match(/^(.+)-(\d+)$/);
  if (slateIdOnly) {
    return {
      name: "",
      playerId: slateIdOnly[2].trim(),
      slateCode: slateIdOnly[1].trim(),
      lockedTag,
    };
  }

  if (/^\d+$/.test(s)) {
    return {
      name: "",
      playerId: s,
      slateCode: "",
      lockedTag,
    };
  }

  return { name: s, playerId: "", slateCode: "", lockedTag };
}

function formatExportCell(site, item, idIndex) {
  const name = String(item?.name || "").trim();
  const playerId = String(item?.playerId || "").trim();
  const slateCode = String(item?.slateCode || "").trim();
  if (!name) return "";

  if (site === "dk") {
    return playerId ? `${name} (${playerId})` : name;
  }

  const display = idIndex?.fdDisplay?.get(name) || name;
  if (playerId && slateCode) return fdNameWithIdRaw(name, playerId, slateCode, display);
  if (playerId && idIndex?.fdPrefix) return fdNameWithIdRaw(name, playerId, idIndex.fdPrefix, display);
  return name;
}

function formatExportCellPreserveOriginal(site, item, idIndex, originalItem, keepOriginal) {
  if (keepOriginal) {
    const raw = String(originalItem?.originalCell || "").trim();
    if (raw) return raw;
  }

  return formatExportCell(site, item, idIndex);
}

function getHeaderIndex(header, names) {
  const canon = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  const map = new Map((header || []).map((h, i) => [canon(h), i]));
  for (const n of names) {
    const idx = map.get(canon(n));
    if (idx != null) return idx;
  }
  return -1;
}

function getContestSlotIndexes(headerRaw, site) {
  const hdr = (headerRaw || []).map((x) => String(x || "").trim().toUpperCase());
  const wantedBase = site === "fd"
    ? ["QB", "RB", "RB", "WR", "WR", "WR", "TE", "FLEX", "DEF"]
    : ["QB", "RB", "RB", "WR", "WR", "WR", "TE", "FLEX", "DST"];

  const used = new Set();
  const indexes = [];

  for (const want of wantedBase) {
    let found = -1;
    for (let i = 0; i < hdr.length; i++) {
      if (used.has(i)) continue;
      if (hdr[i] === want) {
        found = i;
        break;
      }
    }
    if (found < 0) return null;
    used.add(found);
    indexes.push(found);
  }

  return indexes;
}

function parseUploadedCsv(text, site) {
  const rows = csvToRows(text);
  if (!rows.length) throw new Error("CSV is empty.");

  const headerRaw = rows[0] || [];
  const cleanHeader = headerRaw.map((x) => String(x || "").trim().toUpperCase());

  const expectedHeader = SITES[site].exportHeader.map((x) => String(x).toUpperCase());
  const isLineups =
    cleanHeader.length >= expectedHeader.length &&
    expectedHeader.every((v, i) => cleanHeader[i] === v);

  if (isLineups) {
    const expected = canonicalSlotOrder(site);
    const lineups = rows.slice(1).map((r, idx) => {
      const assignment = expected.map((slot, i) => {
        const parsed = parseExportCell(site, r[i] || "");
        return {
          slot,
          originalCell: r[i] || "",
          name: parsed.name,
          playerId: parsed.playerId,
          slateCode: parsed.slateCode,
        };
      });

      return {
        index: idx + 1,
        id: `uploaded_${idx + 1}`,
        sourceRowIndex: idx + 1,
        assignment,
      };
    });

    return {
      kind: "lineups",
      header: headerRaw,
      rawRows: rows,
      lineups,
      contests: [],
      selectedContestName: "",
      contestNameIdx: -1,
      entryIdIdx: -1,
      slotIndexes: expected.map((_, i) => i),
    };
  }

  const entryIdIdx = getHeaderIndex(headerRaw, ["Entry ID", "EntryId", "Entry"]);
  const contestNameIdx = getHeaderIndex(headerRaw, ["Contest Name", "Contest"]);
  const slotIndexes = getContestSlotIndexes(headerRaw, site);

  if (contestNameIdx >= 0 && slotIndexes) {
    const expected = canonicalSlotOrder(site);
    const byContest = new Map();

    rows.slice(1).forEach((r, dataIdx) => {
      const sourceRowIndex = dataIdx + 1;
      const entryId = entryIdIdx >= 0 ? String(r[entryIdIdx] || "").trim() : "";
      const contestNameRaw = String(r[contestNameIdx] || "").trim();
      const hasAnyLineupCell = slotIndexes.some((idx) => String(r[idx] || "").trim() !== "");

      if (!entryId || !contestNameRaw || !hasAnyLineupCell) return;

      const assignment = expected.map((slot, j) => {
        const cellIdx = slotIndexes[j];
        const raw = r[cellIdx] || "";
        const parsed = parseExportCell(site, raw);
        return {
          slot,
          originalCell: raw,
          name: parsed.name,
          playerId: parsed.playerId,
          slateCode: parsed.slateCode,
        };
      });

      const lineup = {
        index: 0,
        id: `contest_${sourceRowIndex}_${entryId}`,
        sourceRowIndex,
        entryId,
        contestName: contestNameRaw,
        assignment,
      };

      if (!byContest.has(contestNameRaw)) byContest.set(contestNameRaw, []);
      byContest.get(contestNameRaw).push(lineup);
    });

    const contests = Array.from(byContest.entries())
      .map(([name, list]) => ({
        name,
        count: list.length,
        lineups: list.map((x, i) => ({ ...x, index: i + 1 })),
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

    return {
      kind: "contest",
      header: headerRaw,
      rawRows: rows,
      contests,
      selectedContestName: contests[0]?.name || "",
      contestNameIdx,
      entryIdIdx,
      slotIndexes,
      lineups: contests[0]?.lineups || [],
    };
  }

  throw new Error(
    `Could not detect CSV type for ${site.toUpperCase()}. Upload either a plain lineup export or a contest entries CSV.`
  );
}

/* -------------------------- backend call -------------------------- */
async function runLateSwapBackend(payload) {
  const res = await fetch(`${API_BASE}/solve_nfl_late_swap`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error(`Late swap failed: HTTP ${res.status}`);
  }

  const data = await res.json();
  if (!data?.success) {
    throw new Error(data?.reason || "No feasible late swap");
  }

  return data;
}

function normalizeBackendAssignment(site, assignment) {
  const order = canonicalSlotOrder(site);
  const bySlot = new Map(
    (assignment || []).map((x) => [String(x.slot || "").trim(), String(x.name || "").trim()])
  );
  return order.map((slot) => ({
    slot,
    name: bySlot.get(slot) || "",
  }));
}

function buildUsageTables(lineups) {
  const playerCounts = new Map();
  const teamLineupCounts = new Map();
  const teamPlayerCounts = new Map();
  const totalLineups = Math.max(lineups?.length || 0, 1);

  for (const lineup of lineups || []) {
    const arr = lineup.finalAssignment || lineup.assignment || [];
    const teamsSeenInLineup = new Set();

    for (const a of arr) {
      const nm = String(a?.name || "").trim();
      if (nm) {
        playerCounts.set(nm, {
          name: nm,
          team: a?.team || "",
          pos: a?.posDisp || "",
          count: (playerCounts.get(nm)?.count || 0) + 1,
        });
      }

      const tm = String(a?.team || "").trim();
      if (tm) {
        teamPlayerCounts.set(tm, (teamPlayerCounts.get(tm) || 0) + 1);
        teamsSeenInLineup.add(tm);
      }
    }

    for (const tm of teamsSeenInLineup) {
      teamLineupCounts.set(tm, (teamLineupCounts.get(tm) || 0) + 1);
    }
  }

  const players = Array.from(playerCounts.values())
    .map((x) => ({
      ...x,
      exposure: (x.count / totalLineups) * 100,
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  const teams = Array.from(teamLineupCounts.entries())
    .map(([team, lineupCount]) => ({
      team,
      count: lineupCount,
      exposure: (lineupCount / totalLineups) * 100,
      avgPlayersPerLineup: (teamPlayerCounts.get(team) || 0) / totalLineups,
    }))
    .sort((a, b) => b.count - a.count || a.team.localeCompare(b.team));

  return { players, teams };
}

function getExportParts({ site, slate, row, originalItem, samePlayer }) {
  if (samePlayer) {
    return {
      playerId: String(originalItem?.playerId || "").trim(),
      slateCode: String(originalItem?.slateCode || "").trim(),
    };
  }

  const mainId = String(row?.site_id_main || "").trim();
  const earlyId = String(row?.site_id_early || "").trim();
  const lateId = String(row?.site_id_late || "").trim();
  const thuMonId = String(row?.site_id_thu_mon || "").trim();
  const rawSlateCode = String(row?.slateCode || "").trim();

  const chosenId =
    slate === "early"
      ? earlyId
      : slate === "late"
      ? lateId
      : slate === "thu_mon"
      ? thuMonId
      : mainId;

  if (site === "fd") {
    const dashIdx = chosenId.lastIndexOf("-");

    if (dashIdx >= 0) {
      return {
        slateCode: chosenId.slice(0, dashIdx).trim(),
        playerId: chosenId.slice(dashIdx + 1).trim(),
      };
    }

    return {
      slateCode: rawSlateCode,
      playerId: chosenId,
    };
  }

  return {
    slateCode: rawSlateCode,
    playerId: chosenId,
  };
}

/* ----------------------------- page ------------------------------- */
export default function NFLLateSwap({ activeTab = "lateSwap", setActiveTab = () => {} }) {
  const { data: mainProjectionData } = useJson(PROJ_SRC);
  const { data: simulationData } = useJson("/data/nfl/classic/latest/playersimulations.json");
  const { data: siteIds } = useJson(SITE_IDS_SRC);
  const { data: qbData } = useJson(QB_DATA_SRC);
  const { data: vegasData } = useJson(VEGAS_SRC);
  const updatedAtText = useLastUpdatedFromMeta(META_SRC);

  const [hideExcluded, setHideExcluded] = useState(false);
  const [theme, setTheme] = useStickyState("nfl.lateswap.theme", "dark");
  const isDark = theme === "dark";
  const T = isDark ? OPT_DARK_THEME : OPT_LIGHT_THEME;

  const cardClass = cx(
    UI.card,
    isDark
      ? "border-slate-800 bg-slate-900/95 shadow-[0_20px_60px_rgba(15,23,42,0.9)]"
      : "border-slate-200 bg-white"
  );

  const ghostBtnClass = cx(
    UI.btn.ghost,
    isDark
      ? "border-slate-700 bg-slate-900/80 text-slate-100 hover:bg-slate-800"
      : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
  );

  const primaryBtnClass = cx(
    UI.btn.primary,
    "bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60"
  );

  const inputClass = cx(
    UI.inputBase,
    isDark
      ? "bg-slate-950/70 border-slate-700 text-slate-100 placeholder:text-slate-500"
      : "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400"
  );

const pillBase =
  "px-3 py-1.5 rounded-full text-sm inline-flex items-center gap-2 transition backdrop-blur-md";

const pillOn = isDark
  ? glass.active
  : "bg-white/70 backdrop-blur-md border border-white/60 text-slate-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]";

const pillOff = isDark
  ? glass.idle
  : "bg-white/55 backdrop-blur-md border border-slate-300/80 text-slate-700 hover:bg-white/80";

  const [site, setSite] = useStickyState("nfl.lateswap.site", "dk");
  const [slate, setSlate] = useState("main");
  const proj = useMemo(() => mergeSimulationProjections(mainProjectionData, simulationData, slate, vegasData), [mainProjectionData, simulationData, slate, vegasData]);
  const [selectedSlate, setSelectedSlate] = useState("main");
  const [isSlatePending, startSlateTransition] = useTransition();
  const selectSlate = (nextSlate) => {
    setSelectedSlate(nextSlate);
    startSlateTransition(() => {
      setSlate(nextSlate);
      setSwappedLineups([]);
    });
  };
  const slateDetails = useMemo(() => buildSlateDetails(siteIds, vegasData, site), [siteIds, vegasData, site]);
  const CFG = SITES[site];

  const [csvName, setCsvName] = useStickyState("nfl.lateswap.csvName", "");
  const [parseError, setParseError] = useStickyState("nfl.lateswap.parseError", "");
  const [statusMsg, setStatusMsg] = useStickyState("nfl.lateswap.statusMsg", "");
  const [uploadBundle, setUploadBundle] = useState(null);
  const [selectedContestName, setSelectedContestName] = useStickyState(
    "nfl.lateswap.selectedContestName",
    ""
  );
  const [importedLineups, setImportedLineups] = useState([]);
  const [swappedLineups, setSwappedLineups] = useState([]);
  const [savedBuilds, setSavedBuilds] = useStickyState("nfl.lateswap.savedBuilds", []);
  const fileRef = useRef(null);

  const [running, setRunning] = useState(false);
  const [showOnlyChanged, setShowOnlyChanged] = useStickyState("nfl.lateswap.showOnlyChanged", false);
  const [useAutoStarted, setUseAutoStarted] = useStickyState("nfl.lateswap.useAutoStarted", true);

  const [q, setQ] = useStickyState("nfl.lateswap.q", "");
  const [posFilter, setPosFilter] = useStickyState("nfl.lateswap.posFilter", "ALL");
  const [hideSiteIdOnly, setHideSiteIdOnly] = useStickyState(
    `nfl.lateswap.${site}.${slate}.hideSiteIdOnly`,
    true
  );
  const [sort, setSort] = useStickyState("nfl.lateswap.sort", { col: "eproj", dir: "desc" });

  const [locks, setLocks] = useStickySet(`nfl.lateswap.${site}.${slate}.locks`, []);
  const [excls, setExcls] = useStickySet(`nfl.lateswap.${site}.${slate}.excls`, []);
  const [boost, setBoost] = useStickyDict(`nfl.lateswap.${site}.${slate}.boost`, {});

  // Custom projection overrides. Blank custom projection = use original projection.
  const [customProj, setCustomProj] = useStickyDict(
    `nfl.lateswap.${site}.${slate}.customProj`,
    {}
  );
  const [projDraft, setProjDraft] = useState({});

  useEffect(() => {
    setProjDraft({});
  }, [site, slate]);

  const [minExp, setMinExp] = useStickyDict(`nfl.lateswap.${site}.${slate}.minExp`, {});
  const [maxExp, setMaxExp] = useStickyDict(`nfl.lateswap.${site}.${slate}.maxExp`, {});
  const [lockedGames, setLockedGames] = useStickySet(`nfl.lateswap.${site}.${slate}.lockedGames`, []);

  const [teamBoost, setTeamBoost] = useStickyDict(
  `nfl.lateswap.${site}.${slate}.teamBoost`,
  {}
);

const [excludedGames, setExcludedGames] = useStickySet(
  `nfl.lateswap.${site}.${slate}.excludedGames`,
  []
);

const [excludedTeams, setExcludedTeams] = useStickySet(
  `nfl.lateswap.${site}.${slate}.excludedTeams`,
  []
);

const [selectedGamesArr, setSelectedGamesArr] = useStickyState(
  `nfl.lateswap.${site}.${slate}.selectedGames`,
  []
);

const selectedGames = useMemo(() => new Set(selectedGamesArr), [selectedGamesArr]);

const setSelectedGames = (arg) => {
  setSelectedGamesArr((prevArr) => {
    const prevSet = new Set(prevArr || []);
    let nextSet;

    if (typeof arg === "function") nextSet = arg(prevSet);
    else if (arg instanceof Set) nextSet = arg;
    else if (Array.isArray(arg)) nextSet = new Set(arg);
    else nextSet = new Set();

    return Array.from(nextSet);
  });
};

  const [randomness, setRandomness] = useStickyState(`nfl.lateswap.${site}.${slate}.randomness`, 0);
  const [lineupPownMax, setLineupPownMax] = useStickyState(
    `nfl.lateswap.${site}.${slate}.lineupPownMax`,
    ""
  );
  const [maxPlayerPown, setMaxPlayerPown] = useStickyState(
    `nfl.lateswap.${site}.${slate}.maxPlayerPown`,
    ""
  );
  const [maxFromTeam, setMaxFromTeam] = useStickyState(
    `nfl.lateswap.${site}.${slate}.maxFromTeam`,
    ""
  );
  const [allowTeInFlex, setAllowTeInFlex] = useStickyState(
    `nfl.lateswap.${site}.${slate}.allowTeInFlex`,
    true
  );
  const [stackAllowRB, setStackAllowRB] = useStickyState(
    `nfl.lateswap.${site}.${slate}.stackAllowRB`,
    false
  );
  const [avoidRbVsOppDst, setAvoidRbVsOppDst] = useStickyState(
    `nfl.lateswap.${site}.${slate}.avoidRbVsOppDst`,
    true
  );
  const [avoidOffenseVsOppDst, setAvoidOffenseVsOppDst] = useStickyState(
    `nfl.lateswap.${site}.${slate}.avoidOffenseVsOppDst`,
    false
  );

  const idIndex = useMemo(() => buildIdIndex(siteIds || {}), [siteIds]);

  const toggleExcludeGame = (gKey) => {
  setExcludedGames((prev) => {
    const next = new Set(prev);
    next.has(gKey) ? next.delete(gKey) : next.add(gKey);
    return next;
  });
};

const toggleExcludeTeam = (teamAbbr) => {
  const t = normTeam(teamAbbr);
  if (!t) return;

  setExcludedTeams((prev) => {
    const next = new Set(prev);
    next.has(t) ? next.delete(t) : next.add(t);
    return next;
  });
};

const bumpTeamBoost = (team, step) =>
  setTeamBoost((m) => ({
    ...m,
    [team]: clamp((m[team] || 0) + step, -6, 6),
  }));

  /* ------------------------ site meta / ids ------------------------ */
  const siteMeta = useMemo(() => {
    const out = {
      dk: { byName: new Map(), byId: new Map() },
      fd: { byName: new Map(), byId: new Map(), defenseByTeam: new Map() },
    };

    const dkRows = Array.isArray(siteIds?.dk) ? siteIds.dk : siteIds?.sites?.dk ?? [];
    const fdRows = Array.isArray(siteIds?.fd) ? siteIds.fd : siteIds?.sites?.fd ?? [];

    const ingest = (siteKey, arr) => {
      for (const r of arr) {
        const name = String(pickFirst(r, ["name", "player", "Player", "Name"]) || "").trim();
        if (!name) continue;

        const rec = {
          name,
          id: String(pickFirst(r, ["id", "ID", "dk_id", "fd_id", "site_id"]) || "").trim() || null,
          salary: num(pickFirst(r, ["salary", "Salary", "sal"]) || 0),
          early_id:
            String(pickFirst(r, ["early_id", "earlyId", "EARLY_ID", "early_site_id"]) || "").trim() ||
            null,
          early_salary: num(pickFirst(r, ["early_salary", "earlySalary", "EARLY_SALARY"]) || 0),
          late_id:
            String(pickFirst(r, ["late_id", "lateId", "LATE_ID", "late_site_id"]) || "").trim() ||
            null,
          late_salary: num(pickFirst(r, ["late_salary", "lateSalary", "LATE_SALARY"]) || 0),
          thu_mon_id: String(r.thu_mon_id || "").trim() || null,
          thu_mon_salary: num(r.thu_mon_salary || 0),
          pos: normPos(
            String(pickFirst(r, ["pos", "Pos", "position", "Position", "rosterPosition"]) || "")
          ),
          slateCode: String(
            pickFirst(r, ["slateId", "slate_id", "groupId", "group_id", "lid", "prefix"]) || ""
          ).trim(),
          displayName: String(pickFirst(r, ["displayName", "Name"]) || name).trim(),
          team: normTeam(pickFirst(r, ["team", "Team", "TEAM", "team_abbr", "TeamAbbrev"]) || ""),
        };

        out[siteKey].byName.set(name, rec);
        out[siteKey].byName.set(normName(name), rec);

        if (siteKey === "fd" && rec.pos === "DST" && rec.team) {
          out.fd.defenseByTeam.set(rec.team, rec);
        }

        if (rec.id) out[siteKey].byId.set(rec.id, rec);
        if (rec.early_id) out[siteKey].byId.set(rec.early_id, rec);
        if (rec.late_id) out[siteKey].byId.set(rec.late_id, rec);
        if (rec.thu_mon_id) out[siteKey].byId.set(rec.thu_mon_id, rec);
      }
    };

    ingest("dk", dkRows);
    ingest("fd", fdRows);

    return out;
  }, [siteIds]);

  function getSlateIdAndSalary(meta, slateKey) {
    if (!meta) return { id: "", salary: 0 };
    if (slateKey === "early") {
      return {
        id: String(meta.early_id || "").trim(),
        salary: num(meta.early_salary),
      };
    }
    if (slateKey === "thu_mon") {
      return { id: String(meta.thu_mon_id || "").trim(), salary: num(meta.thu_mon_salary) };
    }
    if (slateKey === "late") {
      return {
        id: String(meta.late_id || "").trim(),
        salary: num(meta.late_salary),
      };
    }
    return {
      id: String(meta.id || "").trim(),
      salary: num(meta.salary),
    };
  }

  function getSlateTags(meta) {
    if (!meta) return [];
    const tags = [];
    if (String(meta.id || "").trim() && num(meta.salary) > 0) tags.push("main");
    if (String(meta.early_id || "").trim() && num(meta.early_salary) > 0) tags.push("early");
    if (String(meta.late_id || "").trim() && num(meta.late_salary) > 0) tags.push("late");
    if (String(meta.thu_mon_id || "").trim() && num(meta.thu_mon_salary) > 0) tags.push("thu_mon");
    return tags;
  }

  /* ------------------------------ rows ------------------------------ */
  const rowsAll = useMemo(() => {
    const arr = Array.isArray(proj?.rows)
      ? proj.rows
      : Array.isArray(proj?.players)
      ? proj.players
      : Array.isArray(proj?.data)
      ? proj.data
      : Array.isArray(proj)
      ? proj
      : [];

    const siteKey = CFG.key;

    const projectedRows = arr
      .map((r) => {
        const name = r.player ?? r.Player ?? r.Name ?? r.playerName ?? r.name ?? "";
        if (!name) return null;

        const projIdRaw =
          r[`${siteKey}_id`] ??
          r[`${siteKey}Id`] ??
          r[`${siteKey.toUpperCase()} ID`] ??
          r[`${siteKey.toUpperCase()}_ID`] ??
          r.id ??
          r.ID ??
          "";

        const projId = String(projIdRaw ?? "").trim();
        const metaById = projId ? siteMeta?.[siteKey]?.byId?.get(projId) : null;
        const meta =
          metaById ||
          siteMeta?.[siteKey]?.byName?.get(name) ||
          siteMeta?.[siteKey]?.byName?.get(normName(name)) ||
          // FanDuel uses full defense names; projection rows use nicknames.
          (siteKey === "fd" && normPos(pickFirst(r, CFG.posKeys)) === "DST"
            ? siteMeta.fd.defenseByTeam.get(normTeam(
                r.team ?? r.Team ?? r.Tm ?? r.TEAM ?? r.team_abbr ?? r.TeamAbbrev ?? ""
              ))
            : null) ||
          null;

        const slateInfo = getSlateIdAndSalary(meta, slate);
        const slates = getSlateTags(meta);

        const rawPos =
          meta?.pos && String(meta.pos).trim() !== ""
            ? String(meta.pos)
            : String(pickFirst(r, CFG.posKeys) || "");
        const pos = normPos(rawPos);
        if (!pos) return null;

        const team = normTeam(
          r.team ?? r.Team ?? r.Tm ?? r.TEAM ?? r.team_abbr ?? r.TeamAbbrev ?? meta?.team ?? ""
        );
        const opp = normTeam(
          r.opp ?? r.Opp ?? r.OPP ?? r.opponent ?? r.Opponent ?? ""
        );

        const time =
          r.Time ??
          r["Time ET"] ??
          r.Start ??
          r["Start Time"] ??
          r["StartTime"] ??
          r.time ??
          "";

        const salaryFromRow = num(
          r[`${siteKey}_sal`] ?? pickFirst(r, CFG.salKeys) ?? r.Salary ?? r.salary
        );

        let salary = 0;
        if (slateInfo.salary > 0) salary = slateInfo.salary;
        else salary = salaryFromRow;

        const projVal = num(r[`${siteKey}_proj`] ?? pickFirst(r, CFG.projKeys));
        const floor = num(r[`${siteKey}_floor`] ?? pickFirst(r, CFG.floorKeys));
        const ceil = num(
          r[`${siteKey}_ceiling`] ??
            r[`${siteKey}_ceil`] ??
            pickFirst(r, CFG.ceilKeys)
        );
        const pown = pctFrac(r[`${siteKey}_pown`] ?? pickFirst(r, CFG.pownKeys));
        const opt = pctFrac(
          r[`${siteKey}_opt`] ??
            pickFirst(r, [`${siteKey.toUpperCase()} Opt%`, `${siteKey.toUpperCase()} Opt`, "Opt%", "Opt"])
        );

        const val = Number.isFinite(projVal) && salary > 0 ? (projVal / salary) * 1000 : 0;

        return {
          name,
          ratesAvailable: r.ratesAvailable,
          pos,
          eligible: pos === "DST" ? ["DST"] : pos === "TE" ? ["TE"] : [pos],
          team,
          opp,
          game: team && opp ? gameKey(team, opp) : "",
          pairKey: team && opp ? [team, opp].sort().join("@") : "",
          time,
          salary,
          proj: projVal,
          eproj: projVal,
          floor,
          ceil,
          pown: r.ratesAvailable ? pown : null,
          opt: r.ratesAvailable ? opt : null,
          val,
          slates,
          site_id_main: String(meta?.id || "").trim(),
          site_id_early: String(meta?.early_id || "").trim(),
          site_id_late: String(meta?.late_id || "").trim(),
          site_id_thu_mon: String(meta?.thu_mon_id || "").trim(),
          slateCode: String(meta?.slateCode || "").trim(),
          hasProjection: true,
          siteIdOnly: false,
          __raw: r,
        };
      })
      .filter(Boolean);

    const qbArr = Array.isArray(qbData?.rows)
      ? qbData.rows
      : Array.isArray(qbData?.players)
      ? qbData.players
      : Array.isArray(qbData?.data)
      ? qbData.data
      : Array.isArray(qbData)
      ? qbData
      : [];

    const vegasArr = Array.isArray(vegasData?.rows)
      ? vegasData.rows
      : Array.isArray(vegasData?.players)
      ? vegasData.players
      : Array.isArray(vegasData?.data)
      ? vegasData.data
      : Array.isArray(vegasData)
      ? vegasData
      : [];

    const scheduleByTeam = new Map();

    const addSchedule = ({ team, opp, time }) => {
      const t = normTeam(team);
      const o = normTeam(opp);
      if (!t || !o) return;
      const cur = scheduleByTeam.get(t);
      const nextKick = parseKick(time || "");
      const curKick = cur ? parseKick(cur.time || "") : NaN;
      const better =
        !cur ||
        (Number.isFinite(nextKick) && !Number.isFinite(curKick)) ||
        (Number.isFinite(nextKick) && Number.isFinite(curKick) && nextKick < curKick);
      if (better) {
        scheduleByTeam.set(t, {
          team: t,
          opp: o,
          time: time || cur?.time || "",
          game: gameKey(t, o),
          pairKey: [t, o].sort().join("@"),
        });
      }
    };

    for (const r of projectedRows) {
      addSchedule({ team: r.team, opp: r.opp, time: r.time });
    }

    for (const r of qbArr) {
      addSchedule({
        team: r.Team ?? r.team ?? r.Tm ?? r.TEAM,
        opp: r.Opp ?? r.opp ?? r.OPP ?? r.Opponent ?? r.opponent,
        time: r.Time ?? r["Time ET"] ?? r.Start ?? r["Start Time"] ?? r.time,
      });
    }

    // Vegas is the preferred schedule source so late swap matches the optimizer.
    for (const r of vegasArr) {
      const team = normTeam(r.team ?? r.Team ?? "");
      const opp = normTeam(r.opponent ?? r.Opponent ?? r.opp ?? r.Opp ?? "");
      const gameDate = r.gameDate ?? r.game_date ?? "";
      if (!team || !opp) continue;

      const time = formatVegasTime(gameDate);
      addSchedule({ team, opp, time });
      addSchedule({ team: opp, opp: team, time });
    }

    const seenNames = new Set(projectedRows.map((r) => normName(r.name)));
    const seenIds = new Set(
      projectedRows
        .flatMap((r) => [
          r.site_id_main, r.site_id_early, r.site_id_late,
          ...(siteKey === "fd" ? [r.site_id_thu_mon] : []),
        ])
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    );

    const uniqueMetaRows = [];
    const metaSeen = new Set();

    for (const rec of siteMeta?.[siteKey]?.byName?.values?.() || []) {
      if (!rec?.name) continue;

      const slateInfo = getSlateIdAndSalary(rec, slate);
      const slateId = String(slateInfo?.id || "").trim();
      const salary = num(slateInfo?.salary) > 0 ? num(slateInfo.salary) : num(rec.salary);
      const metaKey = slateId || normName(rec.name);

      if (!metaKey || metaSeen.has(metaKey)) continue;
      metaSeen.add(metaKey);

      if (seenNames.has(normName(rec.name)) || (slateId && seenIds.has(slateId))) continue;
      if (!slateId || salary <= 0) continue;

      const pos = normPos(rec.pos);
      if (!pos) continue;

      const team = normTeam(rec.team);
      const sched = team ? scheduleByTeam.get(team) : null;
      const opp = normTeam(sched?.opp || "");
      const time = sched?.time || "";
      const gk = team && opp ? gameKey(team, opp) : "";

      uniqueMetaRows.push({
        name: String(rec.name).trim(),
        pos,
        eligible: pos === "DST" ? ["DST"] : pos === "TE" ? ["TE"] : [pos],
        team,
        opp,
        game: gk,
        pairKey: team && opp ? [team, opp].sort().join("@") : "",
        time,
        salary,
        proj: 0,
        eproj: 0,
        floor: 0,
        ceil: 0,
        pown: 0,
        opt: 0,
        val: 0,
        slates: getSlateTags(rec),
        site_id_main: String(rec.id || "").trim(),
        site_id_early: String(rec.early_id || "").trim(),
        site_id_late: String(rec.late_id || "").trim(),
          site_id_thu_mon: String(rec.thu_mon_id || "").trim(),
        slateCode: String(rec.slateCode || "").trim(),
        hasProjection: false,
        siteIdOnly: true,
        __raw: rec,
      });
    }

    return [...projectedRows, ...uniqueMetaRows];
  }, [proj, CFG, siteMeta, slate, qbData, vegasData]);

const rows = useMemo(() => {
  const filtered = rowsAll.filter((r) => {
    const slateOK = Array.isArray(r.slates)
      ? r.slates.includes(slate)
      : false;

    if (!slateOK) return false;

    // Active-site salary must be valid
    const salary = Number(r.salary) || 0;
    if (salary <= 0) return false;

    // Team must be valid
    const team = String(r.team ?? "").trim().toUpperCase();

    if (
      !team ||
      team === "N/A" ||
      team === "NA" ||
      team === "N.A." ||
      team === "-" ||
      team === "NULL" ||
      team === "NONE"
    ) {
      return false;
    }

    return true;
  });

  return filtered.map((r) => {
    const playerSteps = Number(boost[r.name] || 0);
    const teamSteps = Number(teamBoost[r.team] || 0);

    const rawCustom = customProj?.[r.name];
    const hasCustomProj =
      rawCustom != null && String(rawCustom).trim() !== "";

    const baseProj = Number.isFinite(Number(r.proj))
      ? Number(r.proj)
      : 0;

    const activeProj = hasCustomProj
      ? Math.max(0, num(rawCustom))
      : baseProj;

    const boostedProj =
      activeProj * (1 + 0.03 * playerSteps + 0.05 * teamSteps);

    const boostedVal =
      Number.isFinite(boostedProj) && r.salary > 0
        ? (boostedProj / r.salary) * 1000
        : 0;

    const originalProjForSort = baseProj;

    const originalValForSort =
      Number.isFinite(baseProj) && r.salary > 0
        ? (baseProj / r.salary) * 1000
        : 0;

    return {
      ...r,

      // Display/live values. These update when custom projections or boosts change.
      proj: activeProj,
      baseProj,
      customProj: rawCustom ?? "",
      hasCustomProj,
      eproj: boostedProj,
      val: boostedVal,

      // Stable sort values. Editing/bumping projections should not move rows.
      sortProj: originalProjForSort,
      sortVal: originalValForSort,

      boostSteps: playerSteps + teamSteps,
      playerBoostSteps: playerSteps,
      teamBoostSteps: teamSteps,
    };
  });
}, [rowsAll, slate, boost, teamBoost, customProj]);

  const rowsByName = useMemo(() => new Map(rows.map((r) => [r.name, r])), [rows]);

  const vegasRows = useMemo(() => {
    if (!vegasData) return [];
    return Array.isArray(vegasData?.rows)
      ? vegasData.rows
      : Array.isArray(vegasData?.players)
      ? vegasData.players
      : Array.isArray(vegasData?.data)
      ? vegasData.data
      : Array.isArray(vegasData)
      ? vegasData
      : [];
  }, [vegasData]);

  const teamVegasMap = useMemo(() => {
    const m = new Map();

    for (const r of vegasRows) {
      const team = normTeam(r.team ?? r.Team ?? "");
      const opponent = normTeam(r.opponent ?? r.Opponent ?? r.opp ?? r.Opp ?? "");
      const field = normTeam(r.field ?? r.Field ?? r.home ?? r.Home ?? "");
      if (!team) continue;

      const teamTotalRaw = r.team_total ?? r.teamTotal ?? r["Team Total"];
      const ouRaw = r.over_under ?? r.OU ?? r["O/U"];
      const spreadRaw = r.spread ?? r.Spread;
      const moneylineRaw = r.moneyline ?? r.Moneyline;
      const gameDate = r.gameDate ?? r.game_date ?? "";

      m.set(team, {
        opponent,
        implied:
          teamTotalRaw == null || teamTotalRaw === "" ? null : num(teamTotalRaw),
        ou: ouRaw == null || ouRaw === "" ? null : num(ouRaw),
        spread:
          spreadRaw == null || spreadRaw === "" ? null : num(spreadRaw),
        moneyline:
          moneylineRaw == null || moneylineRaw === "" ? null : num(moneylineRaw),
        isHome: field ? field === team : null,
        field,
        gameDate,
        time: formatVegasTime(gameDate),
      });
    }

    return m;
  }, [vegasRows]);

  const teamImpliedMap = useMemo(() => {
    const m = new Map();
    for (const [team, vals] of teamVegasMap.entries()) {
      if (Number.isFinite(vals?.implied)) m.set(team, vals.implied);
    }
    return m;
  }, [teamVegasMap]);

  const rowByAlias = useMemo(() => {
    const m = new Map();

    for (const r of rows) {
      const nm = String(r.name || "").trim();
      if (nm) {
        m.set(normName(nm), r);
        m.set(nm, r);
      }

      const raw = r.__raw || {};
      const extraNames = [
        raw.name,
        raw.Name,
        raw.player,
        raw.Player,
        raw.playerName,
        raw.displayName,
        raw.DisplayName,
      ]
        .map((x) => String(x || "").trim())
        .filter(Boolean);

      for (const alias of extraNames) {
        m.set(normName(alias), r);
        m.set(alias, r);
      }

      const ids = [r.site_id_main, r.site_id_early, r.site_id_late]
        .map((x) => String(x || "").trim())
        .filter(Boolean);

      for (const id of ids) {
        m.set(`id:${id}`, r);
        if (r.slateCode) m.set(`id:${r.slateCode}-${id}`, r);
      }
    }

    return m;
  }, [rows]);

  const allGames = useMemo(() => {
    const best = new Map();

    for (const r of rows) {
      const team = normTeam(r.team);
      const opp = normTeam(r.opp);
      if (!team || !opp) continue;

      const key = r.game || gameKey(team, opp);
      if (best.has(key)) continue;

      const teamVegas = teamVegasMap.get(team) || {};
      const oppVegas = teamVegasMap.get(opp) || {};

      let home = "";
      if (teamVegas.isHome === true) home = team;
      else if (oppVegas.isHome === true) home = opp;
      else if (teamVegas.field === team || teamVegas.field === opp) home = teamVegas.field;
      else if (oppVegas.field === team || oppVegas.field === opp) home = oppVegas.field;
      if (!home) home = opp;

      const away = home === team ? opp : team;
      const gameDate = teamVegas.gameDate || oppVegas.gameDate || "";
      const displayTime = teamVegas.time || oppVegas.time || formatKick(r.time);
      const rawTime = gameDate || r.time || "";
      const kick = gameDate
        ? vegasKickMinutes(gameDate)
        : (() => {
            const parsed = parseKick(r.time);
            return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
          })();

      best.set(key, {
        key,
        a: away,
        b: home,
        away,
        home,
        rawTime,
        displayTime,
        gameDate,
        kickoffStamp: slateKickoffStamp(gameDate),
        kick,
      });
    }

    return Array.from(best.values()).sort((x, y) => {
      const xd = Number.isFinite(x.kickoffStamp) ? x.kickoffStamp : Number.POSITIVE_INFINITY;
      const yd = Number.isFinite(y.kickoffStamp) ? y.kickoffStamp : Number.POSITIVE_INFINITY;
      if (xd !== yd) return xd - yd;
      const xa = Number.isFinite(x.kick) ? x.kick : Number.POSITIVE_INFINITY;
      const ya = Number.isFinite(y.kick) ? y.kick : Number.POSITIVE_INFINITY;
      return xa - ya || `${x.a}@${x.b}`.localeCompare(`${y.a}@${y.b}`);
    });
  }, [rows, teamVegasMap]);

  const playersByGame = useMemo(() => {
  const m = new Map();
  for (const r of rowsAll) {
    if (!r.game) continue;
    if (!m.has(r.game)) m.set(r.game, new Set());
    m.get(r.game).add(r.name);
  }
  return m;
}, [rowsAll]);

const playersByTeam = useMemo(() => {
  const m = new Map();
  for (const r of rowsAll) {
    if (!r.team) continue;
    if (!m.has(r.team)) m.set(r.team, new Set());
    m.get(r.team).add(r.name);
  }
  return m;
}, [rowsAll]);

const autoExcludedNames = useMemo(() => {
  const out = new Set();

  for (const g of excludedGames) {
    const set = playersByGame.get(g);
    if (set) for (const n of set) out.add(n);
  }

  for (const t of excludedTeams) {
    const set = playersByTeam.get(t);
    if (set) for (const n of set) out.add(n);
  }

  return out;
}, [excludedGames, excludedTeams, playersByGame, playersByTeam]);

const effectiveExcls = useMemo(() => {
  const out = new Set(excls);
  for (const n of autoExcludedNames) out.add(n);
  return out;
}, [excls, autoExcludedNames]);

  const autoLockedGames = useMemo(() => {
    const s = new Set();
    if (!useAutoStarted) return s;
    for (const g of allGames) {
      if (sameDayHasStarted(g.rawTime)) s.add(g.key);
    }
    return s;
  }, [allGames, useAutoStarted]);

  const effectiveLockedGames = useMemo(() => {
    const s = new Set(lockedGames);
    for (const g of autoLockedGames) s.add(g);
    return s;
  }, [lockedGames, autoLockedGames]);

  /* ----------------------- player pool table ----------------------- */
  function positionMatchesFilter(pos, filter) {
    if (filter === "ALL") return true;
    if (filter === "FLEX") return ["RB", "WR", "TE"].includes(pos);
    return pos === filter;
  }

  /* ----------------------- upload / hydrate ----------------------- */
  async function onUploadFile(file) {
    if (!file) return;
    const text = await file.text();
    setCsvName(file.name || "");
    setParseError("");
    setStatusMsg("");

    try {
      const parsed = parseUploadedCsv(text, site);
      setUploadBundle(parsed);
      setSelectedContestName(parsed.selectedContestName || "");
      setSwappedLineups([]);
      setImportedLineups(parsed.lineups || []);
      setStatusMsg(
        parsed.kind === "contest"
          ? `Contest CSV loaded. ${parsed.contests.length} contest(s) found.`
          : `Lineups CSV loaded. ${parsed.lineups.length} lineup(s) found.`
      );
    } catch (e) {
      setUploadBundle(null);
      setImportedLineups([]);
      setSwappedLineups([]);
      setParseError(e?.message || "Could not parse uploaded CSV.");
    }
  }

  useEffect(() => {
    if (!uploadBundle) return;

    if (uploadBundle.kind === "contest") {
      const found =
        uploadBundle.contests.find((c) => c.name === selectedContestName) ||
        uploadBundle.contests[0] ||
        null;
      setImportedLineups(found?.lineups || []);
      if (found && found.name !== selectedContestName) {
        setSelectedContestName(found.name);
      }
    } else {
      setImportedLineups(uploadBundle.lineups || []);
      if (selectedContestName) setSelectedContestName("");
    }

    setSwappedLineups([]);
  }, [uploadBundle, selectedContestName, setImportedLineups, setSelectedContestName, setSwappedLineups]);

  function hydrateLineup(lineup) {
    const assignment = (lineup.assignment || []).map((x) => {
      const rawPlayerId = String(x.playerId || "").trim();
      const rawSlateCode = String(x.slateCode || "").trim();
      const rawName = String(x.name || "").trim();
      const rawOriginalCell = String(x.originalCell || "").trim();

      const parsedOriginal = parseExportCell(site, rawOriginalCell);

      const compositeId =
        rawSlateCode && rawPlayerId ? `${rawSlateCode}-${rawPlayerId}` : "";
      const parsedCompositeId =
        parsedOriginal.slateCode && parsedOriginal.playerId
          ? `${parsedOriginal.slateCode}-${parsedOriginal.playerId}`
          : "";

      const byCompositeId = compositeId ? rowByAlias.get(`id:${compositeId}`) : null;
      const byParsedCompositeId = parsedCompositeId
        ? rowByAlias.get(`id:${parsedCompositeId}`)
        : null;
      const byPlayerId = rawPlayerId ? rowByAlias.get(`id:${rawPlayerId}`) : null;
      const byParsedPlayerId = parsedOriginal.playerId
        ? rowByAlias.get(`id:${parsedOriginal.playerId}`)
        : null;
      const byName = rawName ? rowByAlias.get(normName(rawName)) : null;
      const byParsedName = parsedOriginal.name
        ? rowByAlias.get(normName(parsedOriginal.name))
        : null;

      const row =
        byCompositeId ||
        byParsedCompositeId ||
        byPlayerId ||
        byParsedPlayerId ||
        byName ||
        byParsedName ||
        null;

      const resolvedName = row?.name || parsedOriginal.name || rawName || "";
      const resolvedPlayerId =
        rawPlayerId ||
        (slate === "early"
          ? row?.site_id_early
          : slate === "late"
          ? row?.site_id_late
          : slate === "thu_mon"
          ? row?.site_id_thu_mon
          : row?.site_id_main) ||
        "";
      const resolvedSlateCode = rawSlateCode || parsedOriginal.slateCode || row?.slateCode || "";

      const lockedByGame = row?.game ? effectiveLockedGames.has(row.game) : false;
      const locked = !!parsedOriginal.lockedTag || lockedByGame;

      return {
        ...x,
        name: resolvedName,
        playerId: resolvedPlayerId,
        slateCode: resolvedSlateCode,
        row,
        game: row?.game || "",
        team: row?.team || "",
        opp: row?.opp || "",
        time: row?.time || "",
        salary: row?.salary || 0,
        proj: row?.eproj || 0,
        pown: row?.pown || 0,
        pos: row?.pos || slotBase(x.slot),
        posDisp: row?.pos || slotBase(x.slot),
        locked,
      };
    });

    const lockedAssignment = assignment
      .filter((x) => x.locked && x.name)
      .map((x) => ({ slot: x.slot, name: x.name }));

    const openSlots = CFG.slots.filter(
      (s) => !lockedAssignment.some((x) => x.slot === s.name)
    );

    const lockedSalary = assignment
      .filter((x) => x.locked)
      .reduce((sum, x) => sum + (Number(x.salary) || 0), 0);

    const remainingSalary = CFG.cap - lockedSalary;
    const usedLocked = new Set(lockedAssignment.map((x) => x.name));

    const eligiblePlayers = rows.filter((r) => {
      if (usedLocked.has(r.name)) return false;
      if (r.game && effectiveLockedGames.has(r.game)) return false;
      return true;
    });

    const originalQb = assignment.find((x) => x.pos === "QB" && x.name);
    const qbTeam = originalQb?.team || "";
    const qbOpp = originalQb?.opp || "";
    const sameTeamPassCatchers = assignment.filter(
      (x) =>
        x.name &&
        x.team === qbTeam &&
        ["WR", "TE"].includes(x.pos)
    ).length;
    const sameTeamRB = assignment.filter(
      (x) => x.name && x.team === qbTeam && x.pos === "RB"
    ).length;
    const bringbacks = assignment.filter(
      (x) =>
        x.name &&
        x.team === qbOpp &&
        ["WR", "TE", "RB"].includes(x.pos)
    ).length;

    return {
      ...lineup,
      assignment,
      lockedAssignment,
      openSlots,
      lockedSalary,
      remainingSalary,
      eligiblePlayers,
      originalQbName: originalQb?.name || "",
      originalQbTeam: qbTeam,
      originalQbOpp: qbOpp,
      originalQbStackCount: sameTeamPassCatchers,
      originalQbRbCount: sameTeamRB,
      originalBringbackCount: bringbacks,
    };
  }

  const hydratedImported = useMemo(
    () => importedLineups.map((x) => hydrateLineup(x)),
    [importedLineups, rows, effectiveLockedGames, site, slate]
  );

  const usageSource = useMemo(
    () => (swappedLineups.length ? swappedLineups : hydratedImported),
    [swappedLineups, hydratedImported]
  );

  const lineupUsageByName = useMemo(() => {
    const counts = new Map();
    const source = swappedLineups.length ? swappedLineups : hydratedImported;

    for (const lineup of source) {
      const arr = swappedLineups.length
        ? lineup.finalAssignment || []
        : lineup.assignment || [];

      for (const a of arr) {
        const nm = String(a?.name || "").trim();
        if (!nm || nm === "—") continue;
        counts.set(nm, (counts.get(nm) || 0) + 1);
      }
    }

    return counts;
  }, [swappedLineups, hydratedImported]);

  const lineupUsagePctByName = useMemo(() => {
    const pctMap = new Map();
    const total = Math.max(swappedLineups.length || hydratedImported.length || 0, 1);
    for (const [name, count] of lineupUsageByName.entries()) {
      pctMap.set(name, (count / total) * 100);
    }
    return pctMap;
  }, [lineupUsageByName, swappedLineups.length, hydratedImported.length]);

  const availablePool = useMemo(() => {
    const lockedNamesFromCSV = new Set();
    const source = hydratedImported;

    for (const lineup of source) {
      for (const a of lineup.lockedAssignment || []) {
        if (a.name) lockedNamesFromCSV.add(a.name);
      }
    }

    const needle = String(q || "").trim().toLowerCase();

    let arr = rows
      .filter((r) => {
        if (r.game && effectiveLockedGames.has(r.game)) return false;
        if (lockedNamesFromCSV.has(r.name)) return false;
        if (hideSiteIdOnly && r.siteIdOnly) return false;
        if (selectedGames.size && (!r.game || !selectedGames.has(r.game))) return false;
        if (!positionMatchesFilter(r.pos, posFilter)) return false;

        if (!needle) return true;

        return (
          String(r.name || "").toLowerCase().includes(needle) ||
          String(r.team || "").toLowerCase().includes(needle) ||
          String(r.opp || "").toLowerCase().includes(needle) ||
          String(r.pos || "").toLowerCase().includes(needle) ||
          String(r.salary || "").includes(needle)
        );
      })
      .map((r) => ({
        ...r,
        usedPct: lineupUsagePctByName.get(r.name) || 0,
      }));

    if (sort) {
      const dir = sort.dir === "asc" ? 1 : -1;
      arr = arr.slice().sort((a, b) => {
        let av = a[sort.col];
        let bv = b[sort.col];

        if (sort.col === "eproj") {
          av = a.sortProj ?? a.baseProj ?? a.proj ?? 0;
          bv = b.sortProj ?? b.baseProj ?? b.proj ?? 0;
        }
        if (sort.col === "val") {
          av = a.sortVal ?? 0;
          bv = b.sortVal ?? 0;
        }
        if (sort.col === "pown") {
          av = a.pown || 0;
          bv = b.pown || 0;
        }
        if (sort.col === "usedPct") {
          av = a.usedPct || 0;
          bv = b.usedPct || 0;
        }

        if (typeof av === "string") av = av.toLowerCase();
        if (typeof bv === "string") bv = bv.toLowerCase();

        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
    }

    return arr;
}, [
  rows,
  hydratedImported,
  effectiveLockedGames,
  effectiveExcls,
  hideSiteIdOnly,
  selectedGames,
  posFilter,
  q,
  sort,
  lineupUsagePctByName
]);

  /* ---------------------------- resetters ---------------------------- */
  function clearUpload() {
    setCsvName("");
    setUploadBundle(null);
    setImportedLineups([]);
    setSwappedLineups([]);
    setParseError("");
    setStatusMsg("");
    setSelectedContestName("");
    if (fileRef.current) fileRef.current.value = "";
  }

function resetRules() {
  setLocks(new Set());
  setExcls(new Set());
  setBoost({});
  setTeamBoost({});
  setCustomProj({});
  setProjDraft({});
  setExcludedGames(new Set());
  setExcludedTeams(new Set());
  setSelectedGames(new Set());
  setMinExp({});
  setMaxExp({});
  setRandomness(0);
  setLineupPownMax("");
  setMaxPlayerPown("");
  setMaxFromTeam("");
  setAllowTeInFlex(true);
  setStackAllowRB(false);
  setAvoidRbVsOppDst(true);
  setAvoidOffenseVsOppDst(false);
  setPosFilter("ALL");
  setHideSiteIdOnly(true);
  setQ("");
}

function resetEverything() {
  resetRules();
  clearUpload();
  setLockedGames(new Set());
  setShowOnlyChanged(false);
  setUseAutoStarted(true);
  setSort({ col: "eproj", dir: "desc" });
  setStatusMsg("");
  setParseError("");
}

  /* ----------------------- export swapped csv ----------------------- */
  const fdDefenseIds = useMemo(() => {
    const byName = new Map();
    const byTeam = new Map();
    const add = (name, team, id) => {
      const rawId = String(id || "").trim();
      if (!rawId) return;
      const nameKey = normName(name);
      const teamKey = normTeam(team);
      if (nameKey) byName.set(nameKey, rawId);
      if (teamKey) {
        byTeam.set(teamKey, rawId);
        if (teamKey === "JAC" || teamKey === "JAX") {
          byTeam.set(teamKey === "JAC" ? "JAX" : "JAC", rawId);
        }
      }
    };

    // Match the working optimizer: index DSTs by team and nickname, not
    // only by the full site ID name (Saints vs New Orleans Saints).
    const fdList = Array.isArray(siteIds?.fd) ? siteIds.fd : siteIds?.sites?.fd ?? [];
    for (const rec of fdList) {
      if (normPos(rec.pos ?? rec.Pos ?? rec.position) !== "DST") continue;
      const id = slate === "early" ? rec.early_id : slate === "late" ? rec.late_id : slate === "thu_mon" ? rec.thu_mon_id : rec.id;
      const fullName = String(rec.name ?? rec.player ?? rec.Player ?? "").trim();
      const nickname = fullName.split(/\s+/).pop();
      const team = rec.team ?? rec.Team ?? rec.team_abbr ?? "";
      add(fullName, team, id);
      add(nickname, team, id);
    }

    // FanDuel's uploaded contest template also includes its exact draftables.
    // Prefer those IDs for this particular contest slate when present.
    if (uploadBundle?.kind === "contest") {
      for (const row of uploadBundle.rawRows || []) {
        if (String(row[31] || "").trim().toUpperCase() !== "DEF") continue;
        const id = String(row[15] || "").trim();
        if (!/^\d+-\d+$/.test(id)) continue;
        add(row[18], row[24], id); // full name and team
        add(row[19], row[24], id); // nickname: Saints, Raiders, etc.
      }
    }

    return { byName, byTeam };
  }, [siteIds, slate, uploadBundle]);

  function exportFdDefenseId(item, playerRow, originalItem, keepOriginal) {
    const originalCell = String(originalItem?.originalCell || "").trim();
    if (keepOriginal && /^\d+-\d+(?::.*)?$/.test(originalCell)) return originalCell;

    const team = normTeam(item?.team || item?.row?.team || playerRow?.team || "");
    const name = normName(item?.name);
    const id = fdDefenseIds.byTeam.get(team) || fdDefenseIds.byName.get(name);
    if (!id) throw new Error(`Cannot export ${item?.name || "defense"}: no FanDuel defense ID for this slate.`);
    return id;
  }

  function buildExportRows() {
    const order = canonicalSlotOrder(site);

    if (!swappedLineups.length) return null;

    if (!uploadBundle || uploadBundle.kind === "lineups") {
      const rowsOut = [CFG.exportHeader];

      for (const lineup of swappedLineups) {
        const bySlot = {};
        for (const a of lineup.finalAssignment || []) bySlot[a.slot] = a;

        const row = order.map((slot) => {
          const item = bySlot[slot];
          if (!item?.name) return "";

          const playerRow = rowsByName.get(item.name);
          const originalItem = lineup.assignment.find((a) => a.slot === slot);
          const samePlayer =
            normName(originalItem?.name || "") === normName(item.name || "");

          const keepOriginal = site === "fd" && (!!item.locked || samePlayer);

          if (site === "fd" && slot === "DST") {
            return exportFdDefenseId(item, playerRow, originalItem, keepOriginal);
          }

          const { playerId, slateCode } = getExportParts({
            site,
            slate,
            row: playerRow,
            originalItem,
            samePlayer,
          });

          return formatExportCellPreserveOriginal(
            site,
            {
              name: item.name,
              playerId,
              slateCode,
            },
            idIndex,
            originalItem,
            keepOriginal
          );
        });

        rowsOut.push(row);
      }

      return rowsOut;
    }

    const outRows = (uploadBundle.rawRows || []).map((r) => [...r]);
    const slotIndexes = uploadBundle.slotIndexes || [];

    for (let i = 0; i < swappedLineups.length; i++) {
      const lineup = swappedLineups[i];
      const rowIdx = lineup.sourceRowIndex;
      if (rowIdx == null || !outRows[rowIdx]) continue;

      const bySlot = {};
      for (const a of lineup.finalAssignment || []) bySlot[a.slot] = a;

      order.forEach((slot, j) => {
        const cellIdx = slotIndexes[j];
        const item = bySlot[slot];

        if (!item?.name) {
          outRows[rowIdx][cellIdx] = "";
          return;
        }

        const playerRow = rowsByName.get(item.name);
        const originalItem = lineup.assignment.find((a) => a.slot === slot);
        const samePlayer =
          normName(originalItem?.name || "") === normName(item.name || "");

        const keepOriginal = site === "fd" && (!!item.locked || samePlayer);

        if (site === "fd" && slot === "DST") {
          outRows[rowIdx][cellIdx] = exportFdDefenseId(item, playerRow, originalItem, keepOriginal);
          return;
        }

        const { playerId, slateCode } = getExportParts({
          site,
          slate,
          row: playerRow,
          originalItem,
          samePlayer,
        });

        outRows[rowIdx][cellIdx] = formatExportCellPreserveOriginal(
          site,
          {
            name: item.name,
            playerId,
            slateCode,
          },
          idIndex,
          originalItem,
          keepOriginal
        );
      });
    }

    return outRows;
  }

  function exportSwappedCsv() {
    let rowsOut;
    try {
      rowsOut = buildExportRows();
    } catch (err) {
      setStatusMsg(err?.message || "Could not export swapped lineups.");
      return;
    }
    if (!rowsOut?.length) return;

    const csv = toCsv(rowsOut);
    const baseName = csvName
      ? csvName.replace(/\.csv$/i, "")
      : `nfl_${site}_late_swap_${new Date().toISOString().slice(0, 10)}`;

    const suffix =
      uploadBundle?.kind === "contest" && selectedContestName
        ? `_${selectedContestName.replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "")}_swapped`
        : "_swapped";

    download(`${baseName}${suffix}.csv`, csv);
  }

  /* -------------------------- late swap run -------------------------- */
  function buildLateSwapPayload({
    lineup,
    eligiblePlayers,
    dynamicLocks,
    dynamicExcludes,
    dynamicBoosts,
  }) {
    const openSlots = lineup.openSlots.map((s) => ({
      name: s.name,
      eligible:
        s.name === "FLEX"
          ? allowTeInFlex
            ? ["RB", "WR", "TE"]
            : ["RB", "WR"]
          : s.eligible,
    }));

    const originalLineup = lineup.assignment.map((x) => ({
      slot: x.slot,
      name: x.name,
      pos: x.pos || slotBase(x.slot),
      team: x.team || "",
      opp: x.opp || "",
      salary: x.salary || 0,
      proj: x.proj || 0,
      pown: x.pown || 0,
      locked: !!x.locked,
    }));

    // IMPORTANT: the backend needs the already-locked players in `players` too,
    // not just the open late-swap pool. It uses their player records to recognize
    // the locked QB, locked team counts, locked stack/bring-back pieces and pOWN.
    // They still cannot be selected again because the backend blocks locked_names.
    const payloadPlayerMap = new Map();

    for (const p of eligiblePlayers || []) {
      if (p?.name) payloadPlayerMap.set(p.name, p);
    }

    for (const locked of lineup.lockedAssignment || []) {
      const name = String(locked?.name || "").trim();
      if (!name || payloadPlayerMap.has(name)) continue;

      const row = rowByAlias.get(normName(name));
      const original = lineup.assignment.find(
        (a) => a.slot === locked.slot && normName(a.name) === normName(name)
      );

      payloadPlayerMap.set(name, row || {
        name,
        pos: original?.pos || slotBase(locked.slot),
        team: original?.team || "",
        opp: original?.opp || "",
        salary: original?.salary || 0,
        eproj: original?.proj || 0,
        floor: original?.floor || 0,
        ceil: original?.ceil || 0,
        pown: original?.pown || 0,
        opt: original?.opt || 0,
      });
    }

    const payloadPlayers = Array.from(payloadPlayerMap.values());

    return {
      site,
      cap_remaining: lineup.remainingSalary,
      open_slots: openSlots,
      locked_assignment: lineup.lockedAssignment.map((x) => ({ slot: x.slot, name: x.name })),
      players: payloadPlayers.map((p) => ({
        name: p.name,
        pos: p.pos,
        team: p.team,
        opp: p.opp,
        salary: p.salary,
        proj: p.eproj ?? p.proj ?? 0,
        floor: p.floor || 0,
        ceil: p.ceil || 0,
        pown: p.pown || 0,
        opt: p.opt || 0,
      })),
      objective: "proj",
      boosts: dynamicBoosts,
      randomness: Number(randomness) || 0,
      excludes: Array.from(dynamicExcludes || []),
      locks: Array.from(dynamicLocks || []),
      max_from_team:
        String(maxFromTeam).trim() === "" ? null : Math.max(1, Number(maxFromTeam) || 1),
      lineup_pown_max:
        slate !== "main" || String(lineupPownMax).trim() === "" ? null : Number(lineupPownMax),
      max_player_pown:
        slate !== "main" || String(maxPlayerPown).trim() === "" ? null : Number(maxPlayerPown),

      // Late swap preserves original correlation as a soft preference.
      // Do not hard-enforce new stack/run-back minimums from the frontend.
      qb_stack_min: 0,
      bringback_min: 0,
      allow_te_in_flex: !!allowTeInFlex,
      stack_allow_rb: !!stackAllowRB,
      avoid_rb_vs_opp_dst: !!avoidRbVsOppDst,
      avoid_offense_vs_opp_dst: !!avoidOffenseVsOppDst,


      original_qb_name: lineup.originalQbName || "",
      original_qb_team: lineup.originalQbTeam || "",
      original_qb_opp: lineup.originalQbOpp || "",
      original_qb_stack_count: lineup.originalQbStackCount || 0,
      original_qb_rb_count: lineup.originalQbRbCount || 0,
      original_bringback_count: lineup.originalBringbackCount || 0,

      original_lineup: originalLineup,
    };
  }

  async function runLateSwap() {
    if (!hydratedImported.length) return;

    setRunning(true);
    setStatusMsg("Running backend late swap...");
    setSwappedLineups([]);

    try {
      const out = [];
      const totalLineups = hydratedImported.length;

      // Count exposure that is already unavoidable before we solve anything.
      // This is critical for max exposure: future locked players must count now,
      // otherwise early lineups can consume exposure that is already spoken for.
      const lockedExposureCounts = new Map();

      for (const imported of hydratedImported) {
        for (const a of imported.lockedAssignment || []) {
          const name = String(a?.name || "").trim();
          if (!name) continue;
          lockedExposureCounts.set(
            name,
            (lockedExposureCounts.get(name) || 0) + 1
          );
        }
      }

      // Only players selected into OPEN slots are added here.
      // Locked exposure is already counted above and must not be double-counted.
      const swappedExposureCounts = new Map();

      const getFinalExposureCount = (name) =>
        (lockedExposureCounts.get(name) || 0) +
        (swappedExposureCounts.get(name) || 0);

      const getMaxCount = (name) => {
        const raw = maxExp?.[name];
        if (raw == null || raw === "") return null;
        return Math.floor((Number(raw) / 100) * totalLineups + 1e-9);
      };

      const getMinCount = (name) => {
        const raw = minExp?.[name];
        if (raw == null || raw === "") return null;
        return Math.ceil((Number(raw) / 100) * totalLineups - 1e-9);
      };

      for (let i = 0; i < hydratedImported.length; i++) {
        const lineup = hydratedImported[i];
        setStatusMsg(`Running backend late swap... ${i + 1}/${hydratedImported.length}`);

        const originalOrdered = canonicalSlotOrder(site).map((slot) => {
          const found = lineup.assignment.find((x) => x.slot === slot);
          return { slot, name: found?.name || "" };
        });

        // Nothing can be changed in a fully locked lineup. Its exposure was
        // already included in lockedExposureCounts above.
        if (!lineup.openSlots.length) {
          const finalHydrated = originalOrdered.map((x) => {
            const row = x.name ? rowByAlias.get(normName(x.name)) : null;
            const originalItem = lineup.assignment.find((a) => a.slot === x.slot);
            const samePlayer =
              normName(originalItem?.name || "") === normName(x.name || "");
            const locked = !!lineup.lockedAssignment.find(
              (k) => k.slot === x.slot && k.name === x.name
            );

            let exportPlayerId = "";
            let exportSlateCode = "";

            if (samePlayer) {
              exportPlayerId = originalItem?.playerId || "";
              exportSlateCode = originalItem?.slateCode || "";
            } else {
              exportPlayerId =
                slate === "early"
                  ? row?.site_id_early || ""
                  : slate === "late"
                  ? row?.site_id_late || ""
                  : slate === "thu_mon"
                  ? row?.site_id_thu_mon || ""
                  : row?.site_id_main || "";

              exportSlateCode = row?.slateCode || "";
            }

            return {
              ...x,
              locked,
              row,
              playerId: exportPlayerId,
              slateCode: exportSlateCode,
              salary: row?.salary || 0,
              proj: row?.eproj || 0,
              pown: row?.pown || 0,
              team: row?.team || "",
              opp: row?.opp || "",
              pos: row?.pos || slotBase(x.slot),
              posDisp: row?.pos || slotBase(x.slot),
            };
          });

          out.push({
            ...lineup,
            finalAssignment: finalHydrated,
            finalSalary: finalHydrated.reduce((s, x) => s + (x.salary || 0), 0),
            finalProj: finalHydrated.reduce((s, x) => s + (x.proj || 0), 0),
            finalPown: finalHydrated.reduce((s, x) => s + ((x.pown || 0) * 100), 0),
            changed: false,
            backendError: "",
          });

          continue;
        }

        const dynamicExcludes = new Set(effectiveExcls);
        const dynamicLocks = new Set(locks);
        const dynamicBoosts = { ...boost };

        const remainingIncludingCurrent = totalLineups - i;

        // HARD MAX EXPOSURE.
        // Once locked + newly swapped exposure reaches the requested count,
        // the player is completely unavailable in every remaining open slot.
        for (const r of rows) {
          const maxCt = getMaxCount(r.name);
          if (maxCt == null) continue;

          const used = getFinalExposureCount(r.name);
          if (used >= maxCt) {
            dynamicExcludes.add(r.name);
            dynamicLocks.delete(r.name);
          }
        }

        // MIN EXPOSURE pacing. This never overrides an exclude/max cap.
        // A player only becomes a hard lock when every remaining lineup is
        // needed to reach the requested minimum; otherwise we boost softly.
        for (const r of rows) {
          const minCt = getMinCount(r.name);
          if (minCt == null || minCt <= 0) continue;

          const used = getFinalExposureCount(r.name);
          const need = minCt - used;
          if (need <= 0) continue;
          if (dynamicExcludes.has(r.name)) continue;

          const mustNow = need >= remainingIncludingCurrent;
          if (mustNow) {
            dynamicLocks.add(r.name);
          } else {
            const urgency = need / remainingIncludingCurrent;
            const extra = Math.max(1, Math.min(9, Math.ceil(urgency * 9)));
            dynamicBoosts[r.name] = clamp(
              (dynamicBoosts[r.name] || 0) + extra,
              -9,
              9
            );
          }
        }

        const lockedNamesInLineup = new Set(
          (lineup.lockedAssignment || []).map((x) => x.name)
        );

        const eligiblePlayers = rows.filter((r) => {
          if (lockedNamesInLineup.has(r.name)) return false;
          if (r.game && effectiveLockedGames.has(r.game)) return false;
          if (dynamicExcludes.has(r.name)) return false;
          if (selectedGames.size && (!r.game || !selectedGames.has(r.game))) return false;
          return true;
        });

        if (!eligiblePlayers.length) {
          throw new Error(
            `No eligible players remain for lineup ${i + 1} after locks/excludes/exposure rules.`
          );
        }

        const payload = buildLateSwapPayload({
          lineup,
          eligiblePlayers,
          dynamicLocks,
          dynamicExcludes,
          dynamicBoosts,
        });

        let result;
        try {
          result = await runLateSwapBackend(payload);
        } catch (err) {
          // IMPORTANT: never restore the original lineup here. That could put
          // an explicitly excluded or maxed-out unlocked player back in.
          throw new Error(
            `Late swap stopped at lineup ${i + 1}/${totalLineups}: ${
              err?.message || "No legal late-swap lineup could be generated."
            }`
          );
        }

        const normalized = normalizeBackendAssignment(site, result.assignment || []);
        const changed = JSON.stringify(originalOrdered) !== JSON.stringify(normalized);

        const finalHydrated = normalized.map((x) => {
          const row = x.name ? rowByAlias.get(normName(x.name)) : null;
          const originalItem = lineup.assignment.find((a) => a.slot === x.slot);
          const samePlayer =
            normName(originalItem?.name || "") === normName(x.name || "");
          const locked = !!lineup.lockedAssignment.find(
            (k) => k.slot === x.slot && k.name === x.name
          );

          let exportPlayerId = "";
          let exportSlateCode = "";

          if (samePlayer) {
            exportPlayerId = originalItem?.playerId || "";
            exportSlateCode = originalItem?.slateCode || "";
          } else {
            exportPlayerId =
              slate === "early"
                ? row?.site_id_early || ""
                : slate === "late"
                ? row?.site_id_late || ""
                : slate === "thu_mon"
                ? row?.site_id_thu_mon || ""
                : row?.site_id_main || "";

            exportSlateCode = row?.slateCode || "";
          }

          return {
            ...x,
            locked,
            row,
            playerId: exportPlayerId,
            slateCode: exportSlateCode,
            salary: row?.salary || 0,
            proj: row?.eproj || 0,
            pown: row?.pown || 0,
            team: row?.team || "",
            opp: row?.opp || "",
            pos: row?.pos || slotBase(x.slot),
            posDisp: row?.pos || slotBase(x.slot),
          };
        });

        const lockedSlots = new Set(
          (lineup.lockedAssignment || []).map((x) => x.slot)
        );

        // Final hard safety check: an excluded/maxed player may only appear if
        // that exact slot was already locked and therefore legally unchangeable.
        for (const a of finalHydrated) {
          if (!a.name || lockedSlots.has(a.slot)) continue;

          if (dynamicExcludes.has(a.name) || effectiveExcls.has(a.name)) {
            throw new Error(
              `Late swap returned excluded player ${a.name} in an open slot on lineup ${i + 1}.`
            );
          }

          const maxCt = getMaxCount(a.name);
          if (maxCt != null && getFinalExposureCount(a.name) >= maxCt) {
            throw new Error(
              `Late swap would exceed ${a.name}'s max exposure on lineup ${i + 1}.`
            );
          }
        }

        // Count ONLY newly selected open-slot players. Locked players were
        // pre-counted before the first lineup was solved.
        for (const a of finalHydrated) {
          if (!a.name || lockedSlots.has(a.slot)) continue;

          swappedExposureCounts.set(
            a.name,
            (swappedExposureCounts.get(a.name) || 0) + 1
          );
        }

        out.push({
          ...lineup,
          finalAssignment: finalHydrated,
          finalSalary: finalHydrated.reduce((s, x) => s + (x.salary || 0), 0),
          finalProj: finalHydrated.reduce((s, x) => s + (x.proj || 0), 0),
          finalPown: finalHydrated.reduce((s, x) => s + ((x.pown || 0) * 100), 0),
          changed,
          backendError: "",
        });
      }

      // Final portfolio validation. Locked players can make a requested max
      // mathematically impossible; we report that clearly instead of pretending
      // the optimizer violated the rule in an open slot.
      const warnings = [];

      for (const r of rows) {
        const maxCt = getMaxCount(r.name);
        if (maxCt != null) {
          const lockedCt = lockedExposureCounts.get(r.name) || 0;
          const finalCt = getFinalExposureCount(r.name);

          if (lockedCt > maxCt) {
            warnings.push(
              `${r.name}: max is ${maxCt}/${totalLineups}, but ${lockedCt} lineup(s) are already locked.`
            );
          } else if (finalCt > maxCt) {
            throw new Error(
              `${r.name} finished at ${finalCt}/${totalLineups}, above the hard max of ${maxCt}.`
            );
          }
        }
      }

      setSwappedLineups(out);

      const changedCt = out.filter((x) => x.changed).length;
      setStatusMsg(
        `Finished. ${changedCt} of ${out.length} lineups changed.${
          warnings.length ? ` ${warnings.join(" ")}` : ""
        }`
      );

      const usage = buildUsageTables(out);
      const saved = {
        id: `${Date.now()}`,
        createdAt: new Date().toISOString(),
        site,
        slate,
        csvName,
        uploadKind: uploadBundle?.kind || "lineups",
        selectedContestName:
          uploadBundle?.kind === "contest" ? selectedContestName : "",
        lineupCount: out.length,
        changedCount: changedCt,
        errorCount: 0,
        results: out,
        usage,
      };

      setSavedBuilds((prev) => [saved, ...(prev || [])].slice(0, 12));
    } catch (err) {
      console.error("NFL late swap failed:", err);
      setSwappedLineups([]);
      setStatusMsg(err?.message || "Late swap failed.");
    } finally {
      setRunning(false);
    }
  }


  /* ------------------------------ derived ui ------------------------------ */
  const visibleSwapped = useMemo(() => {
    const base = swappedLineups.length
      ? swappedLineups
      : hydratedImported.map((x) => ({
          ...x,
          finalAssignment: x.assignment.map((a) => ({
            slot: a.slot,
            name: a.name,
            locked: a.locked,
            row: a.row,
            playerId:
              a.playerId ||
              (slate === "early"
                ? a.row?.site_id_early
                : slate === "late"
                ? a.row?.site_id_late
                : slate === "thu_mon"
                ? a.row?.site_id_thu_mon
                : a.row?.site_id_main) ||
              "",
            slateCode: a.slateCode || a.row?.slateCode || "",
            salary: a.salary,
            proj: a.proj,
            pown: a.pown,
            team: a.team,
            opp: a.opp,
            pos: a.pos,
            posDisp: a.posDisp,
          })),
          finalSalary: x.assignment.reduce((s, a) => s + (a.salary || 0), 0),
          finalProj: x.assignment.reduce((s, a) => s + (a.proj || 0), 0),
          finalPown: x.assignment.reduce((s, a) => s + ((a.pown || 0) * 100), 0),
          changed: false,
          backendError: "",
        }));

    return showOnlyChanged ? base.filter((x) => x.changed) : base;
  }, [swappedLineups, hydratedImported, showOnlyChanged, slate]);

  const usageTables = useMemo(() => buildUsageTables(usageSource), [usageSource]);
  const playerUsageRows = usageTables.players;
  const teamUsageRows = usageTables.teams;

  const contestOptions = uploadBundle?.kind === "contest" ? uploadBundle.contests || [] : [];
  const currentContestCount =
    uploadBundle?.kind === "contest"
      ? contestOptions.find((c) => c.name === selectedContestName)?.count || 0
      : 0;

  const th =
    "px-1.5 py-0.5 text-[10px] font-semibold text-center whitespace-nowrap align-middle";
  const td =
    "px-1.5 py-0.5 text-[11px] text-center whitespace-nowrap align-middle";

  const visiblePool = availablePool.filter((r) => !hideExcluded || !(autoExcludedNames.has(r.name) || excls.has(r.name)));

  const toggleSort = (col) =>
    setSort((s) =>
      s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "desc" }
    );

  return (
    <div className={cx(T.page, "px-4 md:px-6 py-5")}>
      <style>{`
        .nfl-lateswap table { width: 100%; border-collapse: collapse; }
        .nfl-lateswap thead th {
          position: sticky;
          top: 0;
          z-index: 40;
          background: ${isDark ? "#020617" : "#f8fafc"} !important;
          color: ${isDark ? "#e5e7eb" : "#0f172a"};
          border-bottom: 1px solid ${isDark ? "rgba(51,65,85,0.95)" : "rgba(203,213,225,0.95)"};
          box-shadow: 0 1px 0 ${isDark ? "rgba(51,65,85,0.8)" : "rgba(203,213,225,0.8)"};
        }
        .nfl-lateswap tbody tr:not(.nfl-excluded-row):nth-child(odd) {
          background: ${isDark ? "rgba(2,6,23,0.88)" : "#ffffff"} !important;
        }
        .nfl-lateswap tbody tr:not(.nfl-excluded-row):nth-child(even) {
          background: ${isDark ? "rgba(15,23,42,0.86)" : "#f8fafc"} !important;
        }
        .nfl-lateswap tbody tr:not(.nfl-excluded-row):hover {
          background: ${isDark ? "rgba(30,41,59,0.92)" : "#f1f5f9"} !important;
        }
        .nfl-lateswap tbody tr.nfl-excluded-row {
          background: ${isDark ? "rgba(15,23,42,0.55)" : "#f1f5f9"} !important;
          opacity: 0.82;
        }
      `}</style>

      <div className="nfl-lateswap">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-x-5 gap-y-3 lg:flex-nowrap">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className={cx("whitespace-nowrap text-2xl md:text-3xl font-extrabold", T.headerText)}>NFL — Late Swap</h1>
            <div className={cx("flex flex-wrap items-baseline gap-x-3 text-xs", T.subtext)}>
              <span className="italic">Updated: {updatedAtText}</span>
              <span>{rows.length} rows</span>
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-wrap items-center gap-3 sm:w-auto sm:shrink-0">
            <div
              className={cx(
                "inline-flex items-center rounded-full p-1 backdrop-blur-md",
                isDark
                  ? glass.shell
                  : "bg-white/70 border border-slate-300/80"
              )}
            >
              <button
                type="button"
                className={cx(
                  "px-3 py-1 text-[11px] font-medium rounded-full transition",
                  isDark
                    ? glass.active
                    : "text-slate-600 hover:bg-white/60"
                )}
                onClick={() => setTheme("dark")}
              >
                Dark
              </button>
              <button
                type="button"
                className={cx(
                  "px-3 py-1 text-[11px] font-medium rounded-full transition",
                  !isDark
                    ? "bg-white/80 border border-white/70 text-slate-900 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
                    : "text-slate-400 hover:text-slate-200 hover:bg-white/6"
                )}
                onClick={() => setTheme("light")}
              >
                Light
              </button>
            </div>

            <div className="flex items-center gap-2">
              <span className={cx("text-xs", T.subtext)}>
                Click here if new slate →
              </span>
              <button
                type="button"
                onClick={resetEverything}
                className="h-8 px-4 rounded-md bg-red-600 text-white text-sm font-semibold shadow-sm transition hover:bg-red-700 active:bg-red-800"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <section aria-label="Slate and site" className={cx(cardClass, "mb-3 p-3")}>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 lg:flex-nowrap">
              <div
  className={cx(
    "inline-flex shrink-0 items-center gap-6 border-b",
    isDark ? "border-slate-800" : "border-slate-300"
  )}
>
  <button
    type="button"
    onClick={() => setActiveTab("optimizer")}
    className={`relative inline-flex h-9 items-center whitespace-nowrap pb-2 text-sm font-semibold transition-colors ${
      activeTab === "optimizer"
        ? isDark
          ? "text-white"
          : "text-slate-950"
        : isDark
        ? "text-slate-500 hover:text-slate-300"
        : "text-slate-500 hover:text-slate-800"
    }`}
  >
    Optimizer

    {activeTab === "optimizer" && (
      <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-sky-400" />
    )}
  </button>

  <button
    type="button"
    onClick={() => setActiveTab("lateSwap")}
    className={`relative inline-flex h-9 items-center whitespace-nowrap pb-2 text-sm font-semibold transition-colors ${
      activeTab === "lateSwap"
        ? isDark
          ? "text-white"
          : "text-slate-950"
        : isDark
        ? "text-slate-500 hover:text-slate-300"
        : "text-slate-500 hover:text-slate-800"
    }`}
  >
    Late Swap

    {activeTab === "lateSwap" && (
      <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-sky-400" />
    )}
  </button>
</div>

              <div className={cx("inline-flex shrink-0 items-center gap-6 border-b", isDark ? "border-slate-800" : "border-slate-200")}>
              {["dk", "fd"].map((s) => (
                <button
                  key={s}
                    onClick={() => {
                      setSite(s);
                      setUploadBundle(null);
                      setImportedLineups([]);
                      setSwappedLineups([]);
                      setParseError("");
                      setCsvName("");
                      setStatusMsg("");
                      setSelectedContestName("");
                      setLocks(new Set());
                      setExcls(new Set());
                      setBoost({});
                      setTeamBoost({});
                      setMinExp({});
                      setMaxExp({});
                      if (fileRef.current) fileRef.current.value = "";
                    }}
                  aria-pressed={site === s}
                  className={`relative inline-flex h-9 cursor-pointer items-center gap-2 whitespace-nowrap pb-2 text-sm font-semibold transition-colors ${site === s ? (isDark ? "text-white" : "text-slate-900") : (isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700")} focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400`}
                >
                  <img src={SITES[s].logo} alt="" className="w-4 h-4" />
                  <span>{SITES[s].label}</span>
                  {site === s && <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full bg-sky-400" />}
                </button>
              ))}
            </div>

    {/* Slate selector */}
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 sm:flex-nowrap">
      <label htmlFor="nfl-lateswap-slate-select" className="text-xs font-medium text-slate-400">Slate:</label>
      <select
        id="nfl-lateswap-slate-select"
        value={selectedSlate}
        aria-busy={isSlatePending}
        onChange={(e) => selectSlate(e.target.value)}
        className={`${isDark ? "bg-slate-950/70 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800"} border h-9 min-w-0 max-w-full flex-1 sm:max-w-md cursor-pointer rounded-md px-3 py-1 text-sm leading-normal transition-colors hover:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400`}
      >
        {[
          { key: "main", label: "Main" },
          { key: "early", label: "Early" },
          { key: "late", label: "Late" },
          { key: "thu_mon", label: "Thu–Mon" },
        ].map((sl) => {
          const detail = slateDetails[sl.key];
          return (
            <option key={sl.key} value={sl.key}>
              {sl.label}{detail ? ` · ${detail.games} games${detail.kickoff ? ` · ${detail.kickoff}` : ""}` : ""}
            </option>
          );
        })}
      </select>
    </div>
          </div>
        </section>
        <section aria-label="Late swap actions" className={cx(cardClass, "mb-3 p-3")}>
          <div className="flex items-center gap-2 flex-wrap">
            <label className={ghostBtnClass + " inline-flex items-center cursor-pointer"}>
              Upload CSV
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => onUploadFile(e.target.files?.[0])}
              />
            </label>

            <button
              className={ghostBtnClass}
              onClick={clearUpload}
              disabled={!importedLineups.length && !csvName}
            >
              Clear
            </button>

            <button
              className={primaryBtnClass}
              onClick={runLateSwap}
              disabled={!importedLineups.length || running}
            >
              {running ? "Running…" : "Run Late Swap"}
            </button>

            <button
              className={ghostBtnClass}
              onClick={exportSwappedCsv}
              disabled={!swappedLineups.length}
            >
              Export Swapped CSV
            </button>
          </div>
          <details className={cx("mt-3 border-t pt-3", isDark ? "border-slate-800" : "border-slate-200")}>
            <summary className="cursor-pointer select-none text-sm font-semibold">Reset Tools</summary>
            <div className="mt-3">
        <div className="flex flex-wrap items-center gap-2">
          <button className={ghostBtnClass} onClick={() => setExcls(new Set())}>
            Clear Excludes
          </button>

          <button className={ghostBtnClass} onClick={() => setLocks(new Set())}>
            Clear Locks
          </button>

          <button
            className={ghostBtnClass}
            onClick={() => {
              setBoost({});
              setMinExp({});
              setMaxExp({});
              setCustomProj({});
              setProjDraft({});
            }}
          >
            Clear boosts/min/max/proj edits
          </button>

          <button className={ghostBtnClass} onClick={resetRules}>Reset All Rules</button>
          <button className={ghostBtnClass} onClick={() => setBoost({})}>Reset Boosts</button>
          <button
            className={ghostBtnClass}
            onClick={() => {
              setCustomProj({});
              setProjDraft({});
            }}
          >
            Reset Proj Edits
          </button>
          <button className={ghostBtnClass} onClick={() => setLocks(new Set())}>Reset Locks</button>
          <button className={ghostBtnClass} onClick={() => setExcls(new Set())}>Reset Excludes</button>
          <button className={ghostBtnClass} onClick={() => { setMinExp({}); setMaxExp({}); }}>
            Reset Exposures
          </button>
          <button
            className={ghostBtnClass}
            onClick={() => {
              setRandomness(0);
              setLineupPownMax("");
              setMaxPlayerPown("");
              setMaxFromTeam("");
                                      setAllowTeInFlex(true);
              setStackAllowRB(false);
              setAvoidRbVsOppDst(true);
              setAvoidOffenseVsOppDst(false);
                                    }}
          >
            Reset Constraints
          </button>
          <button
            className={ghostBtnClass}
            onClick={() => {
              setPosFilter("ALL");
              setQ("");
            }}
          >
            Reset Filters
          </button>
        </div>

            </div>
          </details>
        </section>

        {uploadBundle?.kind === "contest" && (
          <div className={cx(cardClass, "mb-4 p-3")}>
            <div className="text-sm font-semibold mb-2">Contest Selection</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <label className={cx("block text-[10px] mb-1", T.subtext)}>Contest</label>
                <select
                  className={inputClass + " w-full"}
                  value={selectedContestName}
                  onChange={(e) => {
                    setSelectedContestName(e.target.value);
                    setSwappedLineups([]);
                  }}
                >
                  {contestOptions.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name} ({c.count})
                    </option>
                  ))}
                </select>
              </div>

              <div className={cx("text-sm", T.subtext)}>
                <div>
                  Contest lines: <span className="font-semibold">{currentContestCount}</span>
                </div>
                <div>
                  Total contests in file:{" "}
                  <span className="font-semibold">{contestOptions.length}</span>
                </div>
              </div>

              <div className={cx("text-sm", T.subtext)}>
                Export preserves every other contest and only replaces the selected contest’s lineup cells.
              </div>
            </div>
          </div>
        )}

        <div className={cx(cardClass, "mb-4 overflow-hidden")}>
          <div
            className={cx(
              "px-4 py-3 border-b flex items-center justify-between gap-3 flex-wrap",
              isDark ? "border-slate-800 bg-slate-950/45" : "border-slate-200 bg-slate-50/80"
            )}
          >
            <div>
              <div className="text-sm font-semibold">Late Swap Settings</div>
              <div className={cx("mt-0.5 text-[11px]", T.subtext)}>
                Control lineup variance, ownership limits, and roster rules.
              </div>
            </div>

            <button
              type="button"
              className={ghostBtnClass}
              onClick={() => {
                setRandomness(0);
                setMaxFromTeam("");
                setLineupPownMax("");
                setMaxPlayerPown("");
                setAllowTeInFlex(true);
                setStackAllowRB(false);
                setAvoidRbVsOppDst(true);
                setAvoidOffenseVsOppDst(false);
              }}
            >
              Reset Settings
            </button>
          </div>

          <div className="p-4">
            <div className={cx("mb-2 text-[10px] font-semibold uppercase tracking-[0.12em]", T.subtext)}>
              Optimization
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
              {[
                {
                  label: "Randomness %",
                  value: randomness,
                  onChange: setRandomness,
                  placeholder: "0",
                  help: "Adds lineup-to-lineup projection variance.",
                },
                {
                  label: "Max from team",
                  value: maxFromTeam,
                  onChange: setMaxFromTeam,
                  placeholder: "—",
                  help: "Optional cap on players from one team.",
                },
                {
                  label: "Max lineup pOWN%",
                  value: lineupPownMax,
                  onChange: setLineupPownMax,
                  placeholder: "—",
                  help: "Maximum summed projected ownership.",
                },
                {
                  label: "Max player pOWN%",
                  value: maxPlayerPown,
                  onChange: setMaxPlayerPown,
                  placeholder: "—",
                  help: "Exclude players above this ownership.",
                },
              ].map((setting) => (
                <div
                  key={setting.label}
                  className={cx(
                    "rounded-xl border p-3",
                    isDark ? "border-slate-800 bg-slate-950/35" : "border-slate-200 bg-white"
                  )}
                >
                  <label className={cx("block text-[11px] font-semibold mb-1.5", T.headerText)}>
                    {setting.label}
                  </label>
                  <input
                    className={inputClass + " w-full"}
                    disabled={slate !== "main" && setting.label.includes("pOWN")}
                    value={setting.value}
                    onChange={(e) => setting.onChange(e.target.value)}
                    placeholder={setting.placeholder}
                  />
                  <div className={cx("mt-1.5 text-[10px] leading-4", T.subtext)}>
                    {setting.help}
                  </div>
                </div>
              ))}
            </div>

            <div className={cx("mb-2 text-[10px] font-semibold uppercase tracking-[0.12em]", T.subtext)}>
              Lineup Rules
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {[
                {
                  label: "Allow TE in FLEX",
                  description: "TEs can fill the FLEX slot.",
                  checked: allowTeInFlex,
                  onChange: setAllowTeInFlex,
                },
                {
                  label: "Include RB in stacks",
                  description: "RBs may count toward preserved QB correlation.",
                  checked: stackAllowRB,
                  onChange: setStackAllowRB,
                },
                {
                  label: "Avoid RB vs opp DST",
                  description: "Blocks an RB against the opposing defense.",
                  checked: avoidRbVsOppDst,
                  onChange: setAvoidRbVsOppDst,
                },
                {
                  label: "No offense vs opp DST",
                  description: "Blocks all offense against the opposing DST.",
                  checked: avoidOffenseVsOppDst,
                  onChange: setAvoidOffenseVsOppDst,
                },
              ].map((option) => (
                <button
                  key={option.label}
                  type="button"
                  role="switch"
                  aria-checked={option.checked}
                  onClick={() => option.onChange(!option.checked)}
                  className={cx(
                    "w-full min-h-[82px] rounded-xl border px-3.5 py-3",
                    "flex items-center justify-between gap-3 text-left transition",
                    option.checked
                      ? isDark
                        ? "border-sky-500/60 bg-sky-500/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
                        : "border-sky-400 bg-sky-50"
                      : isDark
                      ? "border-slate-800 bg-slate-950/35 hover:border-slate-700 hover:bg-slate-900/70"
                      : "border-slate-200 bg-slate-50/70 hover:border-slate-300 hover:bg-white"
                  )}
                >
                  <span className="min-w-0 pr-2">
                    <span
                      className={cx(
                        "block text-xs font-semibold",
                        option.checked ? "text-sky-400" : T.headerText
                      )}
                    >
                      {option.label}
                    </span>
                    <span className={cx("mt-1 block text-[10px] leading-4", T.subtext)}>
                      {option.description}
                    </span>
                  </span>

                  <span
                    aria-hidden="true"
                    className={cx(
                      "relative inline-flex h-6 w-11 shrink-0 rounded-full border transition-colors",
                      option.checked
                        ? "border-sky-500 bg-sky-500"
                        : isDark
                        ? "border-slate-600 bg-slate-800"
                        : "border-slate-300 bg-slate-300"
                    )}
                  >
                    <span
                      className={cx(
                        "absolute top-0.5 rounded-full bg-white shadow-sm transition-transform",
                        option.checked ? "translate-x-[21px]" : "translate-x-0.5"
                      )}
                      style={{ width: 18, height: 18 }}
                    />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={cx(cardClass, "mb-4 p-3")}>
          <div className="text-sm font-semibold mb-2">Upload / Status</div>
          <div className={cx("text-xs mb-1", T.subtext)}>File: {csvName || "—"}</div>
          <div className={cx("text-xs mb-1", T.subtext)}>
            CSV Type: {uploadBundle?.kind ? uploadBundle.kind.toUpperCase() : "—"}
          </div>
          <div className={cx("text-xs mb-1", T.subtext)}>Imported lineups: {importedLineups.length}</div>
          <div className={cx("text-xs mb-1", T.subtext)}>Current site: {CFG.label}</div>
          <div className={cx("text-xs mb-1", T.subtext)}>Current slate: {slate.toUpperCase()}</div>
          {uploadBundle?.kind === "contest" && (
            <div className={cx("text-xs mb-1", T.subtext)}>
              Selected contest: {selectedContestName || "—"}
            </div>
          )}
          <div className={cx("text-xs mb-2", T.subtext)}>
            Effective locked games: {effectiveLockedGames.size}
          </div>

          {parseError && (
            <div
              className={cx(
                "text-xs px-2 py-1 rounded-md border mb-2",
                isDark
                  ? "bg-rose-500/10 text-rose-200 border-rose-500/25"
                  : "bg-rose-50 text-rose-700 border-rose-200"
              )}
            >
              {parseError}
            </div>
          )}

          {statusMsg && (
            <div
              className={cx(
                "text-xs px-2 py-1 rounded-md border",
                isDark
                  ? "bg-sky-500/10 text-sky-200 border-sky-500/25"
                  : "bg-blue-50 text-blue-700 border-blue-200"
              )}
            >
              {statusMsg}
            </div>
          )}
        </div>

<div className={cx(cardClass, "mb-4 p-3")}>
  <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
    <div>
      <div className="text-sm font-semibold">Games</div>
      <div className={cx("text-[11px] mt-0.5", T.subtext)}>
        Preserve the original correlation structure while still allowing eligible late swaps.
      </div>
    </div>

    <div className="flex items-center gap-2 flex-wrap">
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={useAutoStarted}
          onChange={(e) => setUseAutoStarted(e.target.checked)}
        />
        Auto-lock started games
      </label>

      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={showOnlyChanged}
          onChange={(e) => setShowOnlyChanged(e.target.checked)}
        />
        Show only changed
      </label>
    </div>
  </div>

  <div className="flex items-center gap-2 mb-3 flex-wrap">
    <button
      className={ghostBtnClass}
      onClick={() => setSelectedGames(new Set(allGames.map((g) => g.key)))}
    >
      Select All Games
    </button>

    <button
      className={ghostBtnClass}
      onClick={() => setSelectedGames(new Set())}
    >
      Clear Game Selection
    </button>

    <button
      className={ghostBtnClass}
      onClick={() => setLockedGames(new Set(allGames.map((g) => g.key)))}
    >
      Lock All Games
    </button>

    <button
      className={ghostBtnClass}
      onClick={() => setLockedGames(new Set())}
    >
      Clear Manual Locks
    </button>
  </div>

  <div className="overflow-x-auto">
    <div className="flex gap-2 min-w-max pb-1">
      {allGames.map((g) => {
        const active = selectedGames.has(g.key);
        const manual = lockedGames.has(g.key);
        const auto = autoLockedGames.has(g.key);
        const lockedLabel = manual ? "manual lock" : auto ? "auto lock" : "";

        const away = g.a;
        const home = g.b;

        const awayImp = teamImpliedMap.get(away);
        const homeImp = teamImpliedMap.get(home);
        const gameOu =
          teamVegasMap.get(away)?.ou ??
          teamVegasMap.get(home)?.ou;

        const awayExcluded = excludedTeams.has(away);
        const homeExcluded = excludedTeams.has(home);

        const teamLogoSrc = (abbr) =>
          abbr ? `/logos/nfl/${String(abbr).toUpperCase()}.png` : "";

        return (
          <div
            key={g.key}
            role="button"
            tabIndex={0}
            onClick={() =>
              setSelectedGames((S) => {
                const n = new Set(S);
                active ? n.delete(g.key) : n.add(g.key);
                return n;
              })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setSelectedGames((S) => {
                  const n = new Set(S);
                  active ? n.delete(g.key) : n.add(g.key);
                  return n;
                });
              }
            }}
            className={cx(
              "w-[175px] shrink-0 rounded-xl border overflow-hidden transition-all duration-150 cursor-pointer",
              (manual || auto)
                ? isDark
                  ? "border-red-700/60 bg-red-950/10"
                  : "border-red-300 bg-red-50"
                : active
                ? isDark
                  ? "border-sky-500/60 bg-slate-900/95"
                  : "border-sky-300 bg-white shadow-sm"
                : isDark
                ? "border-slate-800 bg-slate-900/90 hover:border-slate-700"
                : "border-slate-200 bg-white hover:border-slate-300"
            )}
          >
            <div
              className={cx(
                "px-2 py-1.5 border-b",
                (manual || auto)
                  ? isDark
                    ? "border-red-800/40 bg-red-950/20"
                    : "border-red-200 bg-red-50"
                  : isDark
                  ? "border-slate-800 bg-slate-800/40"
                  : "border-slate-200 bg-slate-50"
              )}
            >
              <div className="grid grid-cols-[16px_1fr_20px] items-center gap-1">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedGames((S) => {
                      const n = new Set(S);
                      active ? n.delete(g.key) : n.add(g.key);
                      return n;
                    });
                  }}
                  className={cx(
                    "w-4 h-4 rounded-[4px] border flex items-center justify-center text-[9px] leading-none",
                    active
                      ? isDark
                        ? "border-sky-400 bg-sky-500/15 text-sky-300"
                        : "border-sky-500 bg-sky-50 text-sky-700"
                      : isDark
                      ? "border-slate-700 text-slate-500"
                      : "border-slate-300 text-slate-400"
                  )}
                  title={active ? "Unselect game" : "Select game"}
                  aria-label={active ? "Unselect game" : "Select game"}
                >
                  {active ? "✓" : ""}
                </button>

                <div className="text-center">
                  <div
                    className={cx(
                      "text-[11px] font-semibold whitespace-nowrap",
                      isDark ? "text-slate-300" : "text-slate-600"
                    )}
                  >
                    {g.displayTime || "—"}
                  </div>

                  <div
                    className={cx(
                      "text-[10px] font-semibold whitespace-nowrap",
                      isDark ? "text-slate-500" : "text-slate-500"
                    )}
                  >
                    O/U {Number.isFinite(gameOu) ? fmt1(gameOu) : "—"}
                  </div>

                  <div
                    className={cx(
                      "mt-1 text-center text-[10px] font-semibold",
                      manual
                        ? isDark
                          ? "text-red-300"
                          : "text-red-700"
                        : auto
                        ? isDark
                          ? "text-slate-400"
                          : "text-slate-500"
                        : "text-transparent"
                    )}
                  >
                    {lockedLabel || "placeholder"}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLockedGames((prev) => {
                      const next = new Set(prev);
                      if (next.has(g.key)) next.delete(g.key);
                      else next.add(g.key);
                      return next;
                    });
                  }}
                  className={cx(
                    "w-5 h-5 rounded-md border flex items-center justify-center text-[11px] leading-none",
                    manual
                      ? "bg-red-600 text-white border-red-700"
                      : isDark
                      ? "border-slate-700 text-slate-400 hover:bg-slate-800"
                      : "border-slate-300 text-slate-500 hover:bg-slate-100"
                  )}
                  title={manual ? "Remove manual lock" : "Manual lock game"}
                  aria-label={manual ? "Remove manual lock" : "Manual lock game"}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="px-2 py-2">
              {[
                { abbr: away, spread: teamVegasMap.get(away)?.spread, teamExcluded: awayExcluded },
                { abbr: home, spread: teamVegasMap.get(home)?.spread, teamExcluded: homeExcluded },
              ].map(({ abbr, spread, teamExcluded }, idx) => (
                <div
                  key={`${g.key}-${abbr}-${idx}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExcludeTeam(abbr);
                  }}
                  className={cx(
                    "flex items-center justify-between rounded-lg px-2 py-1.5",
                    idx === 0 ? "mb-1.5" : "",
                    teamExcluded
                      ? isDark
                        ? "bg-red-950/20 ring-1 ring-red-800/40"
                        : "bg-red-50 ring-1 ring-red-200"
                      : isDark
                      ? "bg-slate-800/45"
                      : "bg-slate-50"
                  )}
                  title={teamExcluded ? `Un-exclude ${abbr}` : `Exclude ${abbr}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <img
                      src={teamLogoSrc(abbr)}
                      alt={abbr}
                      className="w-5 h-5 object-contain shrink-0"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    <span
                      className={cx(
                        "font-semibold text-[11px]",
                        isDark ? "text-slate-100" : "text-slate-900"
                      )}
                    >
                      {abbr}
                    </span>
                    {Number.isFinite(Number(spread)) ? (
                      <span className={cx("text-[9px] font-semibold tabular-nums", T.subtext)}>
                        {Number(spread) > 0 ? "+" : ""}
                        {fmt1(Number(spread)).replace(".0", "")}
                      </span>
                    ) : null}
                  </div>

                    <div className="ml-auto flex items-center gap-2 shrink-0">
                      <div className="flex flex-col items-center justify-center leading-none -space-y-[1px]">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            bumpTeamBoost(abbr, +1);
                          }}
                          className={cx(
                            "h-3.5 w-3.5 flex items-center justify-center text-[8px] leading-none rounded-sm transition",
                            (teamBoost[abbr] || 0) > 0
                              ? isDark
                                ? "text-emerald-300 bg-emerald-500/10"
                                : "text-emerald-700 bg-emerald-50"
                              : isDark
                              ? "text-slate-500 hover:text-slate-200 hover:bg-slate-700/40"
                              : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/70"
                          )}
                          title={`Boost ${abbr} offense`}
                        >
                          ▲
                        </button>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            bumpTeamBoost(abbr, -1);
                          }}
                          className={cx(
                            "h-3.5 w-3.5 flex items-center justify-center text-[8px] leading-none rounded-sm transition",
                            (teamBoost[abbr] || 0) < 0
                              ? isDark
                                ? "text-rose-300 bg-rose-500/10"
                                : "text-rose-700 bg-rose-50"
                              : isDark
                              ? "text-slate-500 hover:text-slate-200 hover:bg-slate-700/40"
                              : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/70"
                          )}
                          title={`Reduce ${abbr} offense`}
                        >
                          ▼
                        </button>
                      </div>

                      <div className="min-w-[34px] text-right leading-none">
                        <div
                          className={cx(
                            "text-[15px] font-semibold tabular-nums",
                            isDark ? "text-slate-200" : "text-slate-700"
                          )}
                        >
                          {Number.isFinite(abbr === away ? awayImp : homeImp)
                            ? fmt1(abbr === away ? awayImp : homeImp)
                            : "—"}
                        </div>

                        {!!(teamBoost[abbr] || 0) && (
                          <div
                            className={cx(
                              "text-[9px] tabular-nums mt-0.5",
                              (teamBoost[abbr] || 0) > 0 ? "text-emerald-500" : "text-rose-500"
                            )}
                          >
                            {(teamBoost[abbr] || 0) > 0
                              ? `+${teamBoost[abbr]}`
                              : teamBoost[abbr]}
                          </div>
                        )}
                      </div>

                      {teamExcluded && (
                        <div
                          className={cx(
                            "text-[9px] font-semibold",
                            isDark ? "text-red-300" : "text-red-700"
                          )}
                        >
                          EXCL
                        </div>
                      )}
                    </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  </div>
</div>

        <div className={cx(cardClass, "overflow-hidden mb-4")}>
          <div
            className={cx(
              "px-3 py-1.5 border-b text-sm font-semibold",
              isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
            )}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">Player Pool</h2>
              <span className={cx("text-xs font-normal", T.subtext)}>{visiblePool.length} players shown</span>
            </div>
          </div>
          <div className="px-3 pt-3">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {["ALL", "QB", "RB", "WR", "TE", "FLEX", "DST"].map((p) => (
            <button
              key={p}
              onClick={() => setPosFilter(p)}
              className={
                "relative pb-2 text-xs font-semibold transition " +
                (posFilter === p
                  ? "text-sky-500 after:absolute after:left-0 after:right-0 after:-bottom-px after:h-0.5 after:bg-sky-400"
                  : isDark
                  ? "text-slate-500 hover:text-slate-300"
                  : "text-blue-500 hover:text-blue-600")
              }
              style={{ padding: 0 }}
            >
              {p}
            </button>
          ))}
          <input
            className={inputClass + " w-full sm:ml-auto sm:max-w-xs"}
            placeholder="Search player / team / opp / pos / salary"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <label className={cx(ghostBtnClass, "inline-flex cursor-pointer items-center gap-2 text-xs")}>
            <input type="checkbox" className="h-3.5 w-3.5 accent-sky-500" checked={hideSiteIdOnly} onChange={() => setHideSiteIdOnly((v) => !v)} />
            Hide Unprojected
          </label>
          <label className={cx(ghostBtnClass, "inline-flex cursor-pointer items-center gap-2 text-xs")}>
            <input type="checkbox" className="h-3.5 w-3.5 accent-sky-500" checked={hideExcluded} onChange={(e) => setHideExcluded(e.target.checked)} />
            Hide Excluded
          </label>

          <button
            className={ghostBtnClass}
            onClick={() => {
              setExcls((prev) => {
                const next = new Set(prev);
                const names = availablePool.map((r) => r.name);
                const allExcluded = names.length > 0 && names.every((n) => next.has(n));
                if (allExcluded) names.forEach((n) => next.delete(n));
                else names.forEach((n) => next.add(n));
                return next;
              });
            }}
          >
            Exclude all shown
          </button>

          <button
            className={ghostBtnClass}
            onClick={() => {
              const names = rows
                .filter((r) => positionMatchesFilter(r.pos, posFilter))
                .filter((r) => !hideSiteIdOnly || !r.siteIdOnly)
                .map((r) => r.name);

              setExcls((prev) => {
                const next = new Set(prev);
                const allExcluded = names.length > 0 && names.every((n) => next.has(n));
                if (allExcluded) names.forEach((n) => next.delete(n));
                else names.forEach((n) => next.add(n));
                return next;
              });
            }}
          >
            Exclude position ({posFilter})
          </button>

        </div>

          </div>

          <div className="overflow-auto" style={{ maxHeight: 520 }}>
            <table className={cx("w-max table-auto text-xs", isDark ? "text-slate-100" : "text-slate-900")}>
              <thead className={isDark ? "bg-slate-950" : "bg-slate-50"}>
                <tr>
                  <th className={th}>Lock</th>
                  <th className={th}>Excl</th>
                  <th className={th} onClick={() => toggleSort("pos")} style={{ cursor: "pointer" }}>Pos</th>
                  <th className={th}>Boost</th>
                  <th className={th + " text-left"} onClick={() => toggleSort("name")} style={{ cursor: "pointer" }}>Player</th>
                  <th className={th} onClick={() => toggleSort("team")} style={{ cursor: "pointer" }}>Tm</th>
                  <th className={th} onClick={() => toggleSort("opp")} style={{ cursor: "pointer" }}>Opp</th>
                  <th className={th} onClick={() => toggleSort("salary")} style={{ cursor: "pointer" }}>Salary</th>
                  <th className={th} onClick={() => toggleSort("time")} style={{ cursor: "pointer" }}>Time</th>
                  <th className={th} onClick={() => toggleSort("eproj")} style={{ cursor: "pointer" }}>Proj</th>
                  <th className={th} onClick={() => toggleSort("val")} style={{ cursor: "pointer" }}>Val</th>
                  <th className={th} onClick={() => toggleSort("pown")} style={{ cursor: "pointer" }}>pOWN%</th>
                  <th className={th} onClick={() => toggleSort("usedPct")} style={{ cursor: "pointer" }}>Used %</th>
                  <th className={th}>Min%</th>
                  <th className={th}>Max%</th>
                </tr>
              </thead>

              <tbody>
                  {visiblePool.map((r, idx) => {
                    const locked = locks.has(r.name);
                    const isAutoExcluded = autoExcludedNames.has(r.name);
                    const isManualExcluded = excls.has(r.name);
                    const excluded = isAutoExcluded || isManualExcluded;

                    return (
                    <tr
                      key={r.name}
                      className={cx(
                        "border-t transition",
                        excluded && "nfl-excluded-row",
                        isDark ? "border-slate-800" : "border-slate-200"
                      )}
                    >
                      <td className={td}>
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-blue-500 cursor-pointer"
                          checked={locked}
                          onChange={() =>
                            setLocks((prev) => {
                              const next = new Set(prev);
                              if (next.has(r.name)) next.delete(r.name);
                              else next.add(r.name);
                              return next;
                            })
                          }
                        />
                      </td>

                      <td className={td}>
                        {(() => {
                          const isAutoExcluded = autoExcludedNames.has(r.name);
                          const isManualExcluded = excls.has(r.name);

                          return (
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-blue-500"
                              checked={isAutoExcluded ? true : isManualExcluded}
                              onChange={() => {
                                if (isAutoExcluded) return;
                                setExcls((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(r.name)) next.delete(r.name);
                                  else next.add(r.name);
                                  return next;
                                });
                              }}
                            />
                          );
                        })()}
                      </td>

                      <td className={td}>{r.pos}</td>

                      <td className={td}>
                        <div className="inline-flex items-center gap-1">
                          <button
                            className={cx(
                              "w-6 h-6 rounded-md border px-0",
                              isDark
                                ? "border-slate-700 bg-slate-900/60 text-slate-100 hover:bg-slate-800"
                                : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
                            )}
                            onClick={() =>
                              setBoost((b) => ({
                                ...b,
                                [r.name]: clamp((b[r.name] || 0) - 1, -9, 9),
                              }))
                            }
                          >
                            −
                          </button>

                          <input
                            className={cx(
                              "h-6 w-8 px-1 text-center text-[11px] rounded-md border",
                              isDark
                                ? "bg-slate-950/70 border-slate-700 text-slate-100"
                                : "bg-white border-slate-300 text-slate-800"
                            )}
                            value={boost[r.name] ?? 0}
                            onChange={(e) =>
                              setBoost((b) => ({
                                ...b,
                                [r.name]: clamp(Math.round(+e.target.value || 0), -9, 9),
                              }))
                            }
                          />

                          <button
                            className={cx(
                              "w-6 h-6 rounded-md border px-0",
                              isDark
                                ? "border-slate-700 bg-slate-900/60 text-slate-100 hover:bg-slate-800"
                                : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
                            )}
                            onClick={() =>
                              setBoost((b) => ({
                                ...b,
                                [r.name]: clamp((b[r.name] || 0) + 1, -9, 9),
                              }))
                            }
                          >
                            +
                          </button>
                        </div>
                      </td>

                      <td className={td + " text-left"}>
                        <div className="truncate max-w-[170px]" title={r.name}>
                          {r.name}

                        </div>
                      </td>

                      <td className={td}>
                        <span style={chipStyle(r.team)}>{r.team || "—"}</span>
                      </td>

                      <td className={td}>
                        <span style={chipStyle(r.opp)}>{r.opp || "—"}</span>
                      </td>

                      <td className={td}>{fmt0(r.salary)}</td>
                      <td className={td}>{formatKick(r.time) || ""}</td>
                      <td className={td}>
                        <div className="relative inline-flex items-center justify-center">
                          <input
                            type="number"
                            step="0.1"
                            inputMode="decimal"
                            className="h-7 w-[64px] rounded px-1.5 py-0 text-center text-xs border focus:outline-none focus:ring-1 focus:ring-blue-500"
                            style={{
                              backgroundColor: r.hasCustomProj
                                ? Number(r.proj) > Number(r.baseProj || 0)
                                  ? "rgba(34,197,94,0.14)"
                                  : Number(r.proj) < Number(r.baseProj || 0)
                                  ? "rgba(239,68,68,0.14)"
                                  : isDark
                                  ? "rgba(15,23,42,0.9)"
                                  : "#ffffff"
                                : isDark
                                ? "rgba(15,23,42,0.9)"
                                : "#ffffff",
                              color: r.hasCustomProj
                                ? Number(r.proj) > Number(r.baseProj || 0)
                                  ? "#22c55e"
                                  : Number(r.proj) < Number(r.baseProj || 0)
                                  ? "#ef4444"
                                  : undefined
                                : undefined,
                              borderColor: r.hasCustomProj
                                ? Number(r.proj) > Number(r.baseProj || 0)
                                  ? "rgba(34,197,94,0.7)"
                                  : Number(r.proj) < Number(r.baseProj || 0)
                                  ? "rgba(239,68,68,0.7)"
                                  : isDark
                                  ? "#334155"
                                  : "#cbd5e1"
                                : isDark
                                ? "#334155"
                                : "#cbd5e1",
                            }}
                            value={
                              Object.prototype.hasOwnProperty.call(projDraft, r.name)
                                ? projDraft[r.name]
                                : fmt1(r.eproj ?? r.proj ?? 0)
                            }
                            onFocus={() => {
                              setProjDraft((m) => ({
                                ...m,
                                [r.name]: fmt1(r.eproj ?? r.proj ?? 0),
                              }));
                            }}
                            onBlur={() => {
                              setProjDraft((m) => {
                                const next = { ...m };
                                delete next[r.name];
                                return next;
                              });
                            }}
                            onChange={(e) => {
                              const value = e.target.value;

                              setProjDraft((m) => ({
                                ...m,
                                [r.name]: value,
                              }));

                              setCustomProj((m) => {
                                const next = { ...m };

                                if (value == null || String(value).trim() === "") {
                                  delete next[r.name];
                                  return next;
                                }

                                const shownProj = Number(value);
                                if (!Number.isFinite(shownProj)) return next;

                                const playerSteps = Number(boost[r.name] || 0);
                                const teamSteps = Number(teamBoost[r.team] || 0);
                                const mult = 1 + 0.03 * playerSteps + 0.05 * teamSteps;
                                const baseCustomProj = mult !== 0 ? shownProj / mult : shownProj;

                                next[r.name] = String(Math.max(0, baseCustomProj));
                                return next;
                              });
                            }}
                            title={
                              r.siteIdOnly
                                ? "Type a projection to use this player manually"
                                : "Custom projection override. Boosts are shown in this box."
                            }
                          />

                          {r.hasCustomProj && (
                            <button
                              type="button"
                              className={cx(
                                "absolute -right-1.5 -top-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full border text-[10px] leading-none",
                                isDark
                                  ? "border-slate-700 bg-slate-900/60 text-slate-100 hover:bg-slate-800"
                                  : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
                              )}
                              title="Reset projection"
                              onClick={() => {
                                setCustomProj((m) => {
                                  const next = { ...m };
                                  delete next[r.name];
                                  return next;
                                });
                                setProjDraft((m) => {
                                  const next = { ...m };
                                  delete next[r.name];
                                  return next;
                                });
                              }}
                            >
                              ↺
                            </button>
                          )}
                        </div>
                      </td>
                      <td className={td}>{fmt1(r.val || 0)}</td>
                      <td className={td}>{r.ratesAvailable ? `${fmt1((r.pown || 0) * 100)}%` : "—"}</td>
                      <td className={td}>{fmt1(r.usedPct || 0)}%</td>

                      <td className={td}>
                        <input
                          className={cx(
                            "h-6 w-8 px-1 text-center text-[11px] rounded-md border",
                            isDark
                              ? "bg-slate-950/70 border-slate-700 text-slate-100"
                              : "bg-white border-slate-300 text-slate-800"
                          )}
                          value={minExp[r.name] ?? ""}
                          onChange={(e) =>
                            setMinExp((prev) => ({
                              ...prev,
                              [r.name]: e.target.value === "" ? "" : clamp(Number(e.target.value) || 0, 0, 100),
                            }))
                          }
                        />
                      </td>

                      <td className={td}>
                        <input
                          className={cx(
                            "h-6 w-8 px-1 text-center text-[11px] rounded-md border",
                            isDark
                              ? "bg-slate-950/70 border-slate-700 text-slate-100"
                              : "bg-white border-slate-300 text-slate-800"
                          )}
                          value={maxExp[r.name] ?? ""}
                          onChange={(e) =>
                            setMaxExp((prev) => ({
                              ...prev,
                              [r.name]: e.target.value === "" ? "" : clamp(Number(e.target.value) || 0, 0, 100),
                            }))
                          }
                        />
                      </td>
                    </tr>
                  );
                })}

                {!visiblePool.length && (
                  <tr>
                    <td className={td} colSpan={15}>
                      No available late-swap players for the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
          <div className={cx(cardClass, "overflow-hidden")}>
            <div className={cx("px-3 py-2 border-b text-sm font-semibold", isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50")}>
              Player Usage (used only)
            </div>
            <div className="overflow-auto" style={{ maxHeight: 420 }}>
              <table className="min-w-full w-full text-xs">
                <thead className={isDark ? "bg-slate-950" : "bg-slate-50"}>
                  <tr>
                    <th className={th + " text-left"}>Player</th>
                    <th className={th}>Pos</th>
                    <th className={th}>Tm</th>
                    <th className={th}>Count</th>
                    <th className={th}>Exposure %</th>
                  </tr>
                </thead>
                <tbody>
                  {playerUsageRows.map((r) => {
                    const usageExcluded = effectiveExcls.has(r.name);

                    return (
                    <tr
                      key={r.name}
                      className={cx(
                        "border-t transition",
                        usageExcluded && "nfl-excluded-row",
                        isDark ? "border-slate-800" : "border-slate-200"
                      )}
                    >
                      <td className={td + " text-left"}>{r.name}</td>
                      <td className={td}>{r.pos || ""}</td>
                      <td className={td}><span style={chipStyle(r.team)}>{r.team || "—"}</span></td>
                      <td className={td}>{r.count}</td>
                      <td className={td}>{fmt1(r.exposure)}%</td>
                    </tr>
                    );
                  })}
                  {!playerUsageRows.length && (
                    <tr><td className={td} colSpan={5}>Run or load a build to see usage.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className={cx(cardClass, "overflow-hidden")}>
            <div className={cx("px-3 py-2 border-b text-sm font-semibold", isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50")}>
              Team Usage (used only)
            </div>
            <div className="overflow-auto" style={{ maxHeight: 420 }}>
              <table className="min-w-full w-full text-xs">
                <thead className={isDark ? "bg-slate-950" : "bg-slate-50"}>
                  <tr>
                    <th className={th}>Team</th>
                    <th className={th}>Count</th>
                    <th className={th}>Exposure %</th>
                  </tr>
                </thead>
                <tbody>
                  {teamUsageRows.map((r) => (
                    <tr key={r.team} className={cx("border-t", isDark ? "border-slate-800" : "border-slate-200")}>
                      <td className={td}><span style={chipStyle(r.team)}>{r.team}</span></td>
                      <td className={td}>{r.count}</td>
                      <td className={td}>{fmt1(r.exposure)}%</td>
                    </tr>
                  ))}
                  {!teamUsageRows.length && (
                    <tr><td className={td} colSpan={3}>Run or load a build to see usage.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className={cx(cardClass, "overflow-hidden mb-4")}>
          <div className={cx("px-3 py-2 border-b text-sm font-semibold", isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50")}>
            Imported Lineups
          </div>
          <div className="overflow-auto" style={{ maxHeight: 360 }}>
            <table className="min-w-[1180px] w-full text-xs">
              <thead className={isDark ? "bg-slate-950" : "bg-slate-50"}>
                <tr>
                  <th className={th}>#</th>
                  {uploadBundle?.kind === "contest" && <th className={th}>Entry ID</th>}
                  {uploadBundle?.kind === "contest" && <th className={th + " text-left"}>Contest</th>}
                  <th className={th}>Locked Slots</th>
                  <th className={th}>Open Slots</th>
                  <th className={th}>Locked Salary</th>
                  <th className={th}>Remaining Salary</th>
                  <th className={th}>Orig QB Stack</th>
                  <th className={th + " text-left"}>Players</th>
                </tr>
              </thead>
              <tbody>
                {hydratedImported.map((lineup) => (
                  <tr key={lineup.id} className={cx("border-t", isDark ? "border-slate-800" : "border-slate-200")}>
                    <td className={td}>{lineup.index}</td>
                    {uploadBundle?.kind === "contest" && <td className={td}>{lineup.entryId || "—"}</td>}
                    {uploadBundle?.kind === "contest" && <td className={td + " text-left"}>{lineup.contestName || "—"}</td>}
                    <td className={td}>{lineup.lockedAssignment.length}</td>
                    <td className={td}>{lineup.openSlots.length}</td>
                    <td className={td}>{fmt0(lineup.lockedSalary)}</td>
                    <td className={td}>{fmt0(lineup.remainingSalary)}</td>
                    <td className={td}>
                      {lineup.originalQbTeam
                        ? `${lineup.originalQbTeam} · ${lineup.originalQbStackCount}+${lineup.originalBringbackCount}`
                        : "—"}
                    </td>
                    <td className={td + " text-left"}>
                      <div className="flex flex-wrap gap-1">
                        {lineup.assignment.map((a) => (
                          <span
                            key={`${lineup.id}:${a.slot}`}
                            className={
                              "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 " +
                              (a.locked
                                ? (isDark
                                    ? "bg-rose-500/10 border-rose-400/40 text-rose-200"
                                    : "bg-red-50 border-red-300 text-red-700")
                                : (isDark
                                    ? "bg-sky-900/20 border-sky-800 text-sky-200"
                                    : "bg-blue-50 border-blue-200 text-blue-700"))
                            }
                          >
                            <span className="font-semibold">{a.slot}</span>
                            <span>{a.name || "—"}</span>
                            {a.locked && <span>🔒</span>}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}

                {!hydratedImported.length && (
                  <tr>
                    <td className={td} colSpan={uploadBundle?.kind === "contest" ? 9 : 7}>
                      Upload a lineup CSV or contest entries CSV to begin.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm font-semibold">Swapped Results</div>
          <div className={cx("text-xs", T.subtext)}>
            {uploadBundle?.kind === "contest"
              ? "Selected contest rows will be updated on export; all other contest rows stay untouched."
              : "Preserves lineup order and locked slot positions exactly."}
          </div>
        </div>

        <div className={cx(cardClass, "overflow-hidden mb-4")}>
          <div className="overflow-auto" style={{ maxHeight: 420 }}>
            <table className="min-w-[1420px] w-full text-xs">
              <thead className={isDark ? "bg-slate-950" : "bg-slate-50"}>
                <tr>
                  <th className={th}>#</th>
                  {uploadBundle?.kind === "contest" && <th className={th}>Entry ID</th>}
                  <th className={th}>Changed</th>
                  <th className={th}>Salary</th>
                  <th className={th}>Proj</th>
                  <th className={th}>pOWN%</th>
                  <th className={th}>QB Stack</th>
                  <th className={th + " text-left"}>Final Players</th>
                </tr>
              </thead>
              <tbody>
                {visibleSwapped.map((lineup) => {
                  const finalQb = (lineup.finalAssignment || []).find((x) => x.pos === "QB" && x.name);
                  const sameTeam = (lineup.finalAssignment || []).filter(
                    (x) =>
                      x.name &&
                      x.team === finalQb?.team &&
                      ["WR", "TE", ...(stackAllowRB ? ["RB"] : [])].includes(x.pos)
                  ).length;
                  const bringbacks = (lineup.finalAssignment || []).filter(
                    (x) =>
                      x.name &&
                      finalQb?.opp &&
                      x.team === finalQb.opp &&
                      ["RB", "WR", "TE"].includes(x.pos)
                  ).length;

                  return (
                    <tr key={`swap:${lineup.id}`} className={cx("border-t", isDark ? "border-slate-800" : "border-slate-200")}>
                      <td className={td}>{lineup.index}</td>
                      {uploadBundle?.kind === "contest" && <td className={td}>{lineup.entryId || "—"}</td>}
                      <td className={td}>{lineup.changed ? "Yes" : "No"}</td>
                      <td className={td}>{fmt0(lineup.finalSalary || 0)}</td>
                      <td className={td}>{fmt1(lineup.finalProj || 0)}</td>
                      <td className={td}>{slate === "main" ? fmt1(lineup.finalPown || 0) : "—"}</td>
                      <td className={td}>
                        {finalQb?.team ? `${finalQb.team} · ${sameTeam}+${bringbacks}` : "—"}
                      </td>
                      <td className={td + " text-left"}>
                        <div className="flex flex-wrap gap-1">
                          {(lineup.finalAssignment || []).map((a) => (
                            <span
                              key={`${lineup.id}:${a.slot}:final`}
                              className={
                                "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 " +
                                (a.locked
                                  ? (isDark
                                      ? "bg-rose-500/10 border-rose-400/40 text-rose-200"
                                      : "bg-red-50 border-red-300 text-red-700")
                                  : (isDark
                                      ? "bg-sky-900/20 border-sky-800 text-sky-200"
                                      : "bg-blue-50 border-blue-200 text-blue-700"))
                              }
                            >
                              <span className="font-semibold">{a.slot}</span>
                              <span>{a.name || "—"}</span>
                              {a.locked && <span>🔒</span>}
                            </span>
                          ))}
                        </div>
                        {!!lineup.backendError && (
                          <div className="mt-1 text-[11px] text-red-600">{lineup.backendError}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {!visibleSwapped.length && (
                  <tr>
                    <td className={td} colSpan={uploadBundle?.kind === "contest" ? 8 : 7}>
                      Run Late Swap to generate swapped results.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className={cx(cardClass, "overflow-hidden mb-4")}>
          <div className={cx("px-3 py-2 border-b text-sm font-semibold flex items-center justify-between gap-2 flex-wrap", isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50")}>
            <span>Saved Builds</span>
            <button className={ghostBtnClass} onClick={() => setSavedBuilds([])} disabled={!savedBuilds.length}>
              Clear Saved Builds
            </button>
          </div>

          <div className="overflow-auto" style={{ maxHeight: 260 }}>
            <table className="min-w-[980px] w-full text-xs">
              <thead className={isDark ? "bg-slate-950" : "bg-slate-50"}>
                <tr>
                  <th className={th}>Time</th>
                  <th className={th}>Site</th>
                  <th className={th}>Slate</th>
                  <th className={th}>Type</th>
                  <th className={th + " text-left"}>Contest</th>
                  <th className={th}>Lineups</th>
                  <th className={th}>Changed</th>
                  <th className={th}>Errors</th>
                  <th className={th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {savedBuilds.map((b) => (
                  <tr key={b.id} className={cx("border-t", isDark ? "border-slate-800" : "border-slate-200")}>
                    <td className={td}>{new Date(b.createdAt).toLocaleString()}</td>
                    <td className={td}>{String(b.site || "").toUpperCase()}</td>
                    <td className={td}>{String(b.slate || "").toUpperCase()}</td>
                    <td className={td}>{String(b.uploadKind || "").toUpperCase()}</td>
                    <td className={td + " text-left"}>{b.selectedContestName || "—"}</td>
                    <td className={td}>{b.lineupCount}</td>
                    <td className={td}>{b.changedCount}</td>
                    <td className={td}>{b.errorCount}</td>
                    <td className={td}>
                      <button
                        className={ghostBtnClass}
                        onClick={() => {
                          setSwappedLineups(b.results || []);
                          setStatusMsg(`Loaded saved build from ${new Date(b.createdAt).toLocaleString()}.`);
                        }}
                      >
                        Load
                      </button>
                    </td>
                  </tr>
                ))}
                {!savedBuilds.length && (
                  <tr><td className={td} colSpan={9}>No saved builds yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {visibleSwapped.map((lineup) => (
            <div key={`card:${lineup.id}`} className={cx(cardClass, "p-3")}>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-base font-semibold">
                  Lineup #{lineup.index}
                  {uploadBundle?.kind === "contest" && lineup.entryId ? ` · ${lineup.entryId}` : ""}
                </div>
                <div className={cx("text-[11px]", T.subtext)}>
                  {lineup.changed ? "Changed" : "Unchanged"}
                </div>
              </div>

              <table className="w-full text-xs">
                <thead className={T.subtext}>
                  <tr>
                    <th className="text-left py-1">Slot</th>
                    <th className="text-left">Player</th>
                    <th className="text-right">Proj</th>
                    <th className="text-right">pOWN%</th>
                    <th className="text-right">Sal</th>
                  </tr>
                </thead>
                <tbody>
                  {(lineup.finalAssignment || []).map((a, j) => (
                    <tr key={`${lineup.id}:${a.slot}:${j}`} className={cx("border-t", isDark ? "border-slate-800" : "border-slate-200")}>
                      <td className="py-1">{a.slot}</td>
                      <td className="py-1">
                        <div className="flex items-center gap-2">
                          {a.team && <span style={chipStyle(a.team)}>{a.team}</span>}
                          <span className="whitespace-nowrap">{a.name || "—"}</span>
                          {a.locked && <span title="Locked">🔒</span>}
                        </div>
                      </td>
                      <td className="py-1 text-right">{fmt1(a.proj || 0)}</td>
                      <td className="py-1 text-right">{slate === "main" ? fmt1((a.pown || 0) * 100) : "—"}</td>
                      <td className="py-1 text-right">{fmt0(a.salary || 0)}</td>
                    </tr>
                  ))}
                  <tr className={cx("border-t font-semibold", isDark ? "border-slate-800" : "border-slate-200")}>
                    <td className="py-1">Totals</td>
                    <td />
                    <td className="py-1 text-right">{fmt1(lineup.finalProj || 0)}</td>
                    <td className="py-1 text-right">{slate === "main" ? fmt1(lineup.finalPown || 0) : "—"}</td>
                    <td className="py-1 text-right">{fmt0(lineup.finalSalary || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
